// Facility Detail — tabbed deep view of a single facility.
//
// Tab structure follows the 7-tab spec from the Jack Stuber AM design
// session (2026-05-07), captured in account-management-README.md §
// "Facility Detail — 7-Tab Structure":
//
//   1. Overview   — Pinned issue, recent activity summary, quick actions
//                   (Phase 2 build — placeholder for now)
//   2. Activity   — Unified reverse-chrono feed over projects, service
//                   providers, audit_log and service_assignments.
//                   CORRECTED 2026-08-10 (CW-I Phase 2): this line used to
//                   read "live: feedback only". That was false — the Activity
//                   tab was refactored at some point and lost
//                   facility_feedback as a source entirely, which is how
//                   feedback ended up with no surface anywhere on this page
//                   for months. Feedback now has its own tab (below); do not
//                   fold it back into Activity, and do not describe Activity
//                   as carrying it.
//   2b. Feedback  — facility_feedback rows, reverse-chrono, ALL statuses by
//                   default with an Open-only toggle (live)
//   3. Floorplan  — Building layout, floor types, closet/logbook locations
//                   (Phase 3 build — placeholder for now)
//   4. Supplies   — Per-facility supply view (Phase 2 — wire to Supply
//                   Management module)
//   5. Tasks      — Facility-level task list (live)
//   6. Actions    — Quick-action buttons (Phase 2)
//   7. Info       — Security/access, Site Cheat Sheet, square footage,
//                   Open in Maps, admin metadata (Phase 2)
//
// Visual treatment matches `20-account-management.html` § Facility detail:
// breadcrumb → header (RAG dot + name + ID/type/market chips, AM + open
// counts on the right) → tab bar → tab content. The page is mobile-first;
// the header reflows from a single column on phones to side-by-side on
// tablet+. On phones the tab bar scrolls horizontally.
// ============================================================================

import { hasFinanceCapability, hasFullAmAuthority, seesRateHistory } from '../../shared/role-gates';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AccountStartPanel } from '../new-account-sop/account-start-panel';
import { SmartSelect, isAssignableSub } from '../../components/smart-select';
import {
  NAS_BILLING_METHOD_OPTIONS,
  computeHourlyMonthly,
  type NasBillingMethod,
  type NasSubRateType,
} from '../new-account-sop/lib/service-lines';
import { RecurringServiceSelect } from '../project-work/service-type-select';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  CheckSquare,
  ChevronLeft,
  ClipboardList,
  ExternalLink,
  FileText,
  Info,
  Layers,
  LayoutGrid,
  MessageSquare,
  MessageSquarePlus,
  Package,
  PauseCircle,
  Plus,
  Ruler,
  ShieldCheck,
  Sparkles,
  Zap,
} from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { useMarkets, marketOptions } from '../../shared/markets';
import { useCanManageSubs } from '../../shared/use-can-manage-subs';
import { startLabel } from '../../shared/building-lifecycle';
import { useAuth } from '../../shared/auth';
import { fetchAmNameMap } from '../../shared/am-name';
// From the parts file, not the page: importing the page would pull its own
// Supabase client and router hooks into this module's graph for one chip.
import {
  LifecycleDates,
  ServiceStateChip,
  coversFacilityFilter,
  deriveStopServiceCapabilities,
  lifecycleDateRows,
} from './stop-service-parts';
import {
  Button,
  EmptyState,
  ErrorState,
  FacilityCleaningLines,
  PageHeader,
  RagDot,
  Skeleton,
  TabBar,
} from '../../shared/components';
// CW-C Phase 1 — one navigation definition for the whole app.
import {
  CoordVerifyControl,
  NavigateButton,
  isCoordTrusted,
  type NavTarget,
} from '../../shared/map';
import { useFacilitiesCleaning } from './hooks/use-facility-cleaning';
import {
  RAG_CATEGORY_LABELS,
  RAG_COLORS,
  ragLabel,
  token,
} from '../../shared/design-tokens';
import { fmtBareDate, fmtDate } from '../../shared/format';
import { AISummaryCard } from './ai-summary-card';
import { BuildingCard } from '../../shared/buildings';
import { NoAccountManagerBanner } from './components/no-account-manager-banner';
import type {
  Facility,
  FacilityFeedback,
  FacilityRagHistoryEntry,
  FacilityTask,
} from '../../shared/types';
// ⛔ The five canonical facility types, from the ONE list the app already has.
// Do not write a sixth list here.
import { NAS_FACILITY_TYPE_OPTIONS } from '../../shared/types';
import FeedbackForm from './feedback-form';
import FacilityTasksPane from './facility-tasks-pane';
import { RagEditModal } from './components/rag-edit-modal';
import { FacilityActivityTab } from './components/facility-activity';
import FacilityFeedbackList from './components/facility-feedback-list';
import { FacilityChemicalsPanel } from './components/facility-chemicals-panel';
import { DialysisStockCard } from '../supply-management/dialysis-stock/clinic-stock-card';
// Info-tab Phase 2 editor (2026-05-16) — hooks + components
import {
  useFacilityInfoData,
  type BillingClusterRow,
  type FacilityAccessRow,
  type FacilityContactRow,
  type FacilityDetailsRow,
  type FacilityMarginRow,
  type ServiceAssignmentRow,
} from './hooks/use-facility-info-data';
import { useFacilityFieldUpdate } from './hooks/use-facility-field-update';
import { useRagHistory } from './hooks/use-rag-history';
import { InlineEditField } from './components/inline-edit-field';
// CW-Q Phase 6 — client-rate + margin cells and the rules behind them.
import {
  BilledMonthsRow,
  ClientRateRow,
  MarginRows,
  PausedFieldBadge,
  ScheduleEstimateRow,
} from './components/facility-rate-rows';
import { useBilledMonths } from './hooks/use-billed-months';
import { useScheduledAnnualIncreaseRows } from '../kickers/lib/use-scheduled-annual-increase';
import { scheduledIncreaseFrom } from '../kickers/lib/annual-increase';
import {
  normalizeRateBasis,
  deriveRateBasisFallback,
  HOURLY_ACTUALS_LABEL,
  NO_SET_HOURS_SENTENCE,
  hourlyBilledText,
  isHourlyBilledLine,
  moneyWithCents,
  paysSubByTheHour,
  perHour,
  perHourShort,
  resumesAtHint,
  runningHourlyRate,
  scheduleMonthlyEstimate,
  type RateBasis,
} from '../../shared/rate-basis';
import { RateChangeModal } from './components/rate-change-modal';
import { HospitalLinesCard } from './components/hospital-lines-card';
import { EvsiqCleaningLogCard } from './components/evsiq-cleaning-log-card';
import { EvsiqSurveyorLinksCard } from './components/evsiq-surveyor-links-card';
import { KioskPhotosCard } from './components/kiosk-photos-card';
import { StopServiceLineDialog } from '../new-account-sop/stop-service-line-dialog';
import {
  USABLES_OPTIONS,
  toUsablesChoice,
  fromUsablesChoice,
  usablesLabel,
  // ⛔ The ONE place the fraction ↔ percent arithmetic for a management fee
  // lives. 0.0300 in the database is 3% on the screen.
  pctFromKicker,
  kickerFromPct,
} from '../../shared/facility-billing';
// ⛔ ContactEditModal is no longer imported here: editing a person's details is
// what the Points of Contact window now does, and adding or removing a person
// is the POC manager's job, reached as a route from inside that window. The
// modal itself stays for any caller outside this tab.
import { PocManagerModal } from './components/poc-manager';
import {
  HideBlanksProvider,
} from './components/hide-blanks-context';
import { FacilityNaProvider } from './components/facility-na-context';
import {
  useHideBlanksControl,
} from './components/hide-blanks-context';
import {
  CardWithModeToggle,
  type CardMode,
  type ElsewhereRow,
} from './components/card-with-mode-toggle';
// ⛔ AccountManagerEditField is deliberately NOT imported any more. It saved the
// moment the picker changed, which is a second door beside the card's one Save.
// The AM is now drafted in the window like every other field and written by the
// card's Save through the same assign_facility_am() RPC that component used.
// The component itself stays for any caller outside this tab.
import { useActiveEmployees } from './hooks/use-active-employees';
import { useBddOptions } from './hooks/use-bdd-options';
import {
  facilityTypeLabel,
  refusalForTypeChange,
} from './lib/facility-type-change';
import { refreshReaders, useReader } from '../../shared/save-path';
import { errorMessage } from '../../shared/errors';
// ⛔ The one answer to "is this building live", and the one answer to "does a
// change to this line's money go through the rate-change process". This card
// used to decide both for itself.
import {
  facilityIsLive,
  isRunningOrBooked,
  rateChangeApplies,
} from '../../shared/facility-is-live';
import { SubOnTheLineField } from './components/sub-on-the-line';
import { FacilityDocumentsCard } from './components/facility-documents-card';
import { RemoveNeverStartedLineDialog } from './components/remove-never-started-line';
import { MarkNotStartedDialog, SetLineStartDialog } from './components/not-started-line';
import { notStartedReason } from './lib/not-started';
import {
  DETAIL_FIELD_SPECS,
  planDetailsPatch,
  saveFacilityDetailsFields,
  type DetailFieldSpec,
} from './lib/facility-details';
import { assignFacilityAm, summaryColumnsPatch } from './lib/building-saves';
import { supplyCostElsewhereRow } from './lib/supply-cost-row';

// ----- Feature flags -------------------------------------------------------

// INFO_TAB_PHASE2 — when true, the Info tab fetches and renders the new
// facility_access record (alarm code, lock type, badge requirements, key
// location, site cheat sheet) plus square footage from facility_details_*.
// When false, only the existing address + Open in Maps card is shown
// alongside the InDevelopmentPanel placeholder.
//
// FLIP TO TRUE ONLY AFTER:
//   1. supabase/migrations/20260510_facility_access.sql is applied to prod.
//   2. The facility_access table has at least one row for a smoke-test
//      facility (otherwise the Info tab will show empty rows for every
//      facility — accurate but disorienting).
//
// Until then, the Phase 2 code below is dead but typechecked. Build will
// strip the unreachable branches; runtime stays on the existing UI.
const INFO_TAB_PHASE2 = true;
// The tab ids, as data rather than only as a type, so ?tab= can be validated
// against them at runtime instead of trusting whatever is in the URL bar.
// 'overview' is back ON (item 552, 2026-10-19): it has a tab button again and
// carries the shared-building card for the buildings that have suites.
// 'floorplan' is still PARKED (no data) — its render block exists but no tab
// button reaches it; it stays listed so a stale ?tab=floorplan bookmark
// resolves rather than silently landing on Info.
const TABS = [
  'overview',
  'status',
  'activity',
  'feedback',
  'floorplan',
  'supplies',
  'tasks',
  'actions',
  'info',
] as const;
type Tab = (typeof TABS)[number];

export default function FacilityDetail() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const [facility, setFacility] = useState<Facility | null>(null);
  // building_lifecycle() answer for this building (null when Closed). Read,
  // never computed — it decides whether the header says "Starts <date>".
  const [lifecycle, setLifecycle] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<FacilityFeedback[]>([]);
  const [tasks, setTasks] = useState<FacilityTask[]>([]);
  // ⛔ No "Last cleaning" here (item 552, Ben's pick, 2026-10-19). The tile read
  // cleaning_crew_logs, a PLANNED mirror table that only ever held one
  // building's logs (Surgicenter of Kansas City, stopping 2026-07-16), so it
  // showed "—" everywhere else and a three-month-old date there. It comes back
  // when EVSiQ's cleaning logs feed the app — read them then, not this table.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The active tab lives in the URL (?tab=actions), not in component state.
  //
  // Two things need that. The Stop Service page sends you back here to the
  // Actions tab, which is where the Finance/HR acknowledgement chain lives —
  // and Ben's ruling on acks is that "the link brings them to the place where
  // the acknowledgment sits", which a link cannot do if the tab is private
  // state. Second, the browser Back button now returns to the tab you were on.
  //
  // 'info' is the landing tab, so it is represented by NO parameter rather than
  // ?tab=info: the plain facility URL keeps working and stays canonical.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(tabParam ?? '')
    ? (tabParam as Tab)
    : 'info';
  const setTab = useCallback(
    (next: Tab) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'info') params.delete('tab');
      else params.set('tab', next);
      // replace, not push: flicking through tabs should not fill the history
      // stack with entries the Back button has to walk out of.
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  /**
   * The banner's one button. There is exactly one door onto the account
   * manager -- the Account Summary window on the Info tab -- so this lands on
   * it rather than opening a second one. Same reasoning as the missing-address
   * prompt, which stopped carrying its own save for this exact reason.
   */
  const goToAssignAccountManager = useCallback(() => {
    setTab('info');
    // The Info tab mounts on the next paint; the card carries scroll-mt-24.
    window.setTimeout(() => {
      document
        .getElementById('info-card-account-summary')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, [setTab]);

  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  // `quiet` re-reads without flipping `loading`. ⛔ A loader must not flip
  // loading on a refetch — the skeleton unmounts the card mid-save (TCSD-0039).
  async function refresh(quiet = false) {
    if (!facilityId) return;
    if (!quiet) setLoading(true);
    setError(null);
    try {
      // 2026-05-12 repoint note: facility-detail.tsx intentionally stays on
      // the base `facilities` table. AM Info-tab edits, churn analysis, and
      // post-mortems must remain reachable for closed facilities (e.g. the
      // FAC-0242 close_reason backfill). Operational lists (my-accounts,
      // dashboard, supply, hiring) use the active_facilities view instead.
      const [fac, fb, tk, lc] = await Promise.all([
        supabase
          .from('facilities')
          .select('*')
          .eq('facility_id', facilityId)
          .maybeSingle<Facility>(),
        supabase
          .from('facility_feedback')
          .select('*')
          .eq('facility_id', facilityId)
          .order('feedback_date', { ascending: false })
          .limit(100),
        supabase
          .from('facility_tasks')
          .select('*')
          .eq('facility_id', facilityId)
          .order('created_date', { ascending: false })
          .limit(100),
        supabase
          .from('v_account_management_cards')
          .select('lifecycle')
          .eq('facility_id', facilityId)
          .maybeSingle<{ lifecycle: string | null }>(),
      ]);
      if (fac.error) throw fac.error;
      if (lc.error) throw lc.error;
      if (fb.error) throw fb.error;
      if (tk.error) throw tk.error;
      // AM display name resolves from account_manager_eid (the denormalised
      // account_manager_name column was retired 2026-06-08). The base
      // `facilities` row no longer carries the name, so resolve it via the
      // canonical v_am_name view (works for every role).
      let facRow = fac.data;
      if (facRow) {
        // One lookup for both people on the building: the manager who runs it
        // and the BD who won it. Two eids, one round trip.
        const eids = [facRow.account_manager_eid, facRow.sold_by_eid].filter(
          (e): e is string => !!e,
        );
        const nameMap = eids.length > 0 ? await fetchAmNameMap(eids) : null;
        facRow = {
          ...facRow,
          account_manager_name: facRow.account_manager_eid
            ? nameMap?.get(facRow.account_manager_eid) ?? null
            : null,
          sold_by_name: facRow.sold_by_eid
            ? nameMap?.get(facRow.sold_by_eid) ?? null
            : null,
        };
      }
      setFacility(facRow);
      setLifecycle(lc.data?.lifecycle ?? null);
      setFeedback((fb.data ?? []) as FacilityFeedback[]);
      setTasks((tk.data ?? []) as FacilityTask[]);
    } catch (e) {
      console.error('[facility-detail] load failed', e);
      setError(
        errorMessage(e, 'Failed to load facility.'),
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  // ⛔ THIS PAGE NOW SAYS WHAT IT READS — 2026-09-16 (info-tab-one-edit-door).
  //
  // Found by smoke test, not by reading the code: changing the management fee
  // wrote 2.5% to the database and the card went on showing 3%. Every write on
  // this tab goes through commitWrite and declares `touches: ['facilities']`,
  // and that declaration was reaching NOBODY, because the loader that reads the
  // facility row had never registered itself. Cards were getting by on
  // hand-passed callbacks, so whether a saved value appeared depended on which
  // callback that card happened to be given.
  //
  // A write that lands while the screen keeps the old value is the exact defect
  // save-path exists to close (PLAYBOOK §15.2), and it is the one that has
  // people save twice. Registering the loader fixes it for every writer,
  // including ones nobody has written yet.
  const quietRefresh = useCallback(() => {
    void refresh(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);
  useReader(['facilities', 'facility_feedback', 'facility_tasks'], quietRefresh);

  if (loading && !facility) {
    return <FacilityDetailSkeleton />;
  }
  if (error) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <BackLink />
        <ErrorState
          title="Couldn't load this facility"
          body={error}
          onRetry={() => refresh()}
        />
      </div>
    );
  }
  if (!facility) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <BackLink />
        <EmptyState
          title="Facility not found"
          body="The facility ID in the URL doesn't match anything in your scope."
          cta={
            <Button as={Link} variant="secondary" size="sm" to="/account-management">
              Back to My Accounts
            </Button>
          }
        />
      </div>
    );
  }

  // Tasks count is shown as a TabBar badge so the AM sees at-a-glance how
  // many open items are waiting on this facility. Computed live from the
  // tasks array we already loaded above. As of 2026-05-19 the denormalised
  // facilities.open_task_count / open_feedback_count columns are dropped;
  // these in-memory counts are the only source of truth.
  const openTaskCount = tasks.filter(
    (t) => t.status === 'Open' || t.status === 'In_Progress',
  ).length;
  const openFeedbackCount = feedback.filter((f) =>
    f.status === 'Open' || f.status === 'In_Progress' || f.status === 'Recurring',
  ).length;
  // Latest feedback_date across the 100-row sample — sorted desc above so [0]
  // is the most recent. Fine for the Overview Status card "Last feedback"
  // tile; pagination beyond 100 is unlikely for a single facility.
  const lastFeedbackDate = feedback[0]?.feedback_date ?? null;

  // 2026-09-25 — the "Starts M/D" badge is gated on the database lifecycle:
  // a confirmed Active building never says it is about to start.
  // 2026-06-15 (Jack) — pre-start facilities should NOT show a "started"/
  // operational-RAG treatment. When the start date is still in the future we
  // render a "Starts M/D" badge in place of the operational RAG dot. Stored
  // facility.status is left untouched (Ben owns that) — presentation only.
  const futureStartLabel = startLabel(lifecycle, facility.planned_start_date);

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-6">
      <BackLink />

      {facility.status === 'Closed' && (
        <ClosedFacilityBanner
          closedDate={facility.closed_date}
          closeReason={facility.close_reason}
        />
      )}

      <PageHeader
        eyebrow={
          <span className="flex items-center gap-1.5">
            <span className="font-mono">{facility.facility_id}</span>
            {facility.facility_type && (
              <>
                <span aria-hidden="true">·</span>
                <span>{facility.facility_type}</span>
              </>
            )}
            {facility.market_region && (
              <>
                <span aria-hidden="true">·</span>
                <span>{facility.market_region}</span>
              </>
            )}
          </span>
        }
        title={
          <span className="flex items-center gap-3">
            {futureStartLabel ? (
              <span className="inline-flex items-center rounded-full border border-st-info-border bg-st-info-bg px-2.5 py-0.5 text-xs font-semibold text-st-info-fg-strong">
                {futureStartLabel}
              </span>
            ) : (
              <RagDot status={facility.account_status_rag} size="lg" />
            )}
            <span>{facility.facility_name}</span>
            {/* Service state is the SECOND lifecycle axis and it renders next
                to the name because it is the fact that changes what anyone
                does next: a paused facility looks completely normal on every
                other line of this card. Renders nothing when service is
                Active, which is the overwhelming majority. */}
            <ServiceStateChip state={facility.service_state} />
          </span>
        }
        meta={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {/* Operational RAG dot — non-interactive here (click-to-edit
                lives in the Status tab). */}
            <span className="flex items-center gap-3">
              {/* 2026-06-15 — pre-start: suppress the operational dot (the
                  "Starts M/D" badge in the title stands in for it). */}
              {futureStartLabel ? (
                <span className="flex items-center gap-1.5">
                  <span className="inline-flex items-center rounded-full border border-st-info-border bg-st-info-bg px-2 py-0.5 text-[11px] font-medium text-st-info-fg-strong">
                    {futureStartLabel}
                  </span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <RagDot status={facility.account_status_rag} />
                  <span className="text-[11px] uppercase tracking-wide text-tcs-text-muted">
                    Operational
                  </span>
                </span>
              )}
            </span>
            {facility.account_manager_name ? (
              <span>AM: {facility.account_manager_name}</span>
            ) : (
              /* ⛔ 2026-09-17: this used to render NOTHING when nobody was
                 recorded, so a building with no owner looked exactly like a
                 building that was fine. HCRM Fertility was live for three days
                 that way. An absence is said out loud. */
              <span className="font-semibold text-rag-red">No account manager</span>
            )}
            {/* When service is paused, the only two things anyone asks are
                "why" and "when are they back". Both go here rather than
                two tabs away. */}
            {(facility.service_state ?? 'Active') === 'Suspended' && (
              <span>
                Paused{' '}
                {facility.expected_resume_date
                  ? `· back ${fmtDate(facility.expected_resume_date)}`
                  : '· no return date'}
              </span>
            )}
            <span>
              {openTaskCount} task · {openFeedbackCount} feedback
            </span>
          </span>
        }
        actions={
          <Button
            size="sm"
            icon={<MessageSquarePlus className="h-4 w-4" />}
            onClick={() => setShowFeedbackForm(true)}
          >
            New feedback
          </Button>
        }
      />

      <NoAccountManagerBanner facility={facility} onFix={goToAssignAccountManager} />

      <div className="mt-4 -mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
        <TabBar
          activeId={tab}
          onChange={setTab}
          // 2026-07-23 — Card tab order reworked (P1); Overview + Floorplan
          // parked for "no data today". 2026-10-19 (item 552, Ben's go on the
          // demo) — Overview is back in its original first place: twelve
          // buildings now carry suites. The card still LANDS ON INFO (no ?tab=
          // parameter). Floorplan stays parked: no button reaches it.
          tabs={[
            {
              id: 'overview',
              label: 'Overview',
              icon: <Sparkles className="h-4 w-4" />,
            },
            {
              id: 'info',
              label: 'Info',
              icon: <Info className="h-4 w-4" />,
            },
            {
              id: 'status',
              label: 'Status',
              icon: <ShieldCheck className="h-4 w-4" />,
            },
            {
              id: 'activity',
              label: 'Activity',
              icon: <ClipboardList className="h-4 w-4" />,
            },
            // CW-I Phase 2 — feedback had no surface at all. The rows were
            // fetched (line ~194) purely to produce two numbers; nothing ever
            // rendered one. Payton logged FBK-0254 here and could not find it.
            {
              id: 'feedback',
              label: 'Feedback',
              count: openFeedbackCount,
              icon: <MessageSquare className="h-4 w-4" />,
            },
            {
              id: 'tasks',
              label: 'Tasks',
              count: openTaskCount,
              icon: <CheckSquare className="h-4 w-4" />,
            },
            {
              id: 'actions',
              label: 'Actions',
              icon: <Zap className="h-4 w-4" />,
            },
            {
              id: 'supplies',
              label: 'Supplies',
              icon: <Package className="h-4 w-4" />,
            },
          ]}
        />
      </div>

      <div className="mt-4">
        {tab === 'overview' && (
          <OverviewTab
            facility={facility}
            openFeedbackCount={openFeedbackCount}
            openTaskCount={openTaskCount}
            lastFeedbackDate={lastFeedbackDate}
            onJumpToTab={setTab}
          />
        )}
        {tab === 'status' && (
          <StatusTab facility={facility} onSaved={() => refresh()} />
        )}
        {tab === 'activity' && (
          <FacilityActivityTab facilityId={facility.facility_id} />
        )}
        {tab === 'feedback' && <FacilityFeedbackList feedback={feedback} />}
        {tab === 'floorplan' && <FloorplanTab />}
        {tab === 'supplies' && <SuppliesTab facility_id={facility.facility_id} />}
        {tab === 'tasks' && (
          <FacilityTasksPane
            facility_id={facility.facility_id}
            account_id={facility.account_id ?? null}
          />
        )}
        {tab === 'actions' && <ActionsTab facility={facility} />}
        {tab === 'info' && (
          <div className="space-y-4">
            {/* Account start — confirm go-live + correct the actual start date
                (drives billing/kicker/proration), incl. accounts stuck Active
                without a real start date (PR-4a). */}
            <AccountStartPanel
              facilityId={facility.facility_id}
              plannedStartDate={facility.planned_start_date ?? null}
              actualStartDate={facility.contract_start ?? null}
              isStarted={facility.status === 'Active'}
              onDone={() => refresh()}
            />
            <InfoTab facility={facility} onFacilityChange={() => refresh()} />
          </div>
        )}
      </div>

      {showFeedbackForm && (
        <FeedbackForm
          facility={facility}
          onClose={() => setShowFeedbackForm(false)}
          onSaved={() => {
            setShowFeedbackForm(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ----- Sub-components ------------------------------------------------------

function BackLink() {
  return (
    <Link
      to="/account-management"
      className="mb-2 inline-flex items-center gap-1 text-xs text-tcs-text-secondary hover:text-tcs-text"
    >
      <ChevronLeft className="h-3.5 w-3.5" />
      My Accounts
    </Link>
  );
}

function FacilityDetailSkeleton() {
  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 md:p-6">
      <Skeleton width={120} height={12} block />
      <div className="space-y-2 border-b border-tcs-border pb-4">
        <Skeleton width={180} height={12} block />
        <Skeleton width="60%" height={28} block />
        <Skeleton width="40%" height={14} block />
      </div>
      <Skeleton width="100%" height={40} block />
      <div className="space-y-2">
        <Skeleton width="100%" height={64} block />
        <Skeleton width="100%" height={64} block />
        <Skeleton width="100%" height={64} block />
      </div>
    </div>
  );
}
// ----- Tab implementations -------------------------------------------------

// Generic "in development" panel used for tabs whose data exists but whose
// UI hasn't been built yet. Distinct from EmptyState (which is for "you have
// no data") and from the placeholder Skeleton (which is for "data is loading").
// `phase` clarifies WHEN the work happens; `body` describes WHAT's coming.
function InDevelopmentPanel({
  icon,
  title,
  phase,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  phase: string;
  body: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-8 text-center">
      <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-tcs-surface text-tcs-text-secondary">
        {icon}
      </div>
      <h2 className="mt-4 text-lg font-semibold text-tcs-text">{title}</h2>
      <div className="mt-1 inline-block rounded bg-tcs-surface px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-tcs-text-muted">
        {phase}
      </div>
      <div className="mx-auto mt-3 max-w-md text-sm text-tcs-text-secondary">
        {body}
      </div>
    </div>
  );
}

// Overview — Phase 2 build per AM README §"Facility Detail — 7-Tab Structure".
// Until pinned-issue/recent-completions/last-touchpoint queries are wired,
// surfaces what we know about the facility today: RAG status, AM, monthly
// rate, market, account name. Acts as a quick-summary card so the tab isn't
// totally empty.
function OverviewTab({
  facility,
  openFeedbackCount,
  openTaskCount,
  lastFeedbackDate,
  onJumpToTab,
}: {
  facility: Facility;
  openFeedbackCount: number;
  openTaskCount: number;
  lastFeedbackDate: string | null;
  onJumpToTab: (tab: Tab) => void;
}) {
  return (
    <div className="space-y-4">
      <AISummaryCard
        facility_id={facility.facility_id}
        onJumpToTab={onJumpToTab}
      />
      {/* Facility Relationships (2026-07-17) — shared-location card. Renders
          only when this facility belongs to a building. Read-only over
          billing; toggle writes display prefs only. */}
      {facility.building_id && (
        <BuildingCard
          buildingId={facility.building_id}
          currentFacilityId={facility.facility_id}
        />
      )}
      <CleaningContactCard facility={facility} />
      {SURGICAL_FACILITY_TYPES.has(facility.facility_type ?? '') && (
        <SurgicalRoomsCard facility={facility} />
      )}
      <dl className="grid grid-cols-2 gap-3 rounded-xl border border-tcs-border bg-tcs-bg p-5 md:grid-cols-4">
        <OverviewStat
          label="RAG"
          value={facility.account_status_rag ?? '—'}
        />
        <OverviewStat
          label="Account"
          value={facility.account_id ?? '—'}
          mono
        />
        <OverviewStat
          label="Market"
          value={facility.market_region ?? '—'}
        />
        <OverviewStat
          label="Type"
          value={facility.facility_type ?? '—'}
        />
        <OverviewStat
          label="Open feedback"
          value={String(openFeedbackCount)}
          mono
        />
        <OverviewStat
          label="Open tasks"
          value={String(openTaskCount)}
          mono
        />
        {/* feedback_date is a bare DATE: fmtBareDate, never new Date() —
            fmtDate showed it a day early west of Greenwich (TCSD-0070). */}
        <OverviewStat
          label="Last feedback"
          value={fmtBareDate(lastFeedbackDate) || '—'}
        />
      </dl>
    </div>
  );
}

function OverviewStat({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
        {label}
      </dt>
      <dd className={`mt-1 text-sm text-tcs-text ${mono ? 'font-mono' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

// 2026-05-30 — "Who's cleaning / who to call" at-a-glance card on the
// Overview tab. Reuses the same batched hook + presentational component as the
// My Accounts list card, so the two surfaces never drift. interactive=true
// here renders the POC phone/email as tappable tel:/mailto: links (the card is
// not wrapped in an <a>, so nested links are fine).
function CleaningContactCard({ facility }: { facility: Facility }) {
  const idArr = useMemo(() => [facility.facility_id], [facility.facility_id]);
  const { cleaning, loading } = useFacilitiesCleaning(idArr);
  const info = cleaning.get(facility.facility_id) ?? null;

  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5">
      <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
        Service
      </h3>
      {loading && !info ? (
        <Skeleton width="60%" height={14} block />
      ) : info ? (
        <FacilityCleaningLines info={info} interactive />
      ) : (
        <p className="text-[12px] italic text-tcs-text-muted">
          No active service assignment on file.
        </p>
      )}
    </div>
  );
}

// AFB-0032 (Jack, 2026-06-12) — surface OR/PR room counts on surgery-center
// facilities. The counts live on service_assignments.or_count/pr_count (the
// active assignment) and are also captured on the New Account SOP. We read the
// active assignment first; if no live assignment carries them yet (e.g.
// Creekwood FAC-0260, which has a SOP with or_count=4 but no active assignment)
// we fall back to the facility's SOP row so the value the AM entered at
// onboarding still shows. Editing stays in the Info tab Crew & Sub block (per
// assignment) and the New Account SOP — this card is read-only surfacing.
// WS5 2026-07-31: 'ASC' + 'Endoscopy Center' removed — renamed to
// Surgery_Center in data (facilities_facility_type_canonical).
const SURGICAL_FACILITY_TYPES = new Set([
  'Surgery_Center',
  'Hospital',
]);

function SurgicalRoomsCard({ facility }: { facility: Facility }) {
  const [orCount, setOrCount] = useState<number | null>(null);
  const [prCount, setPrCount] = useState<number | null>(null);
  const [source, setSource] = useState<'assignment' | 'sop' | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // 1) Active service assignment — the operational source of truth.
        const { data: aRows, error: aErr } = await supabase
          .from('service_assignments')
          .select('or_count, pr_count')
          .eq('facility_id', facility.facility_id)
          .eq('assignment_status', 'Active');
        if (aErr) throw aErr;
        const aHit = ((aRows ?? []) as Array<{
          or_count: number | null;
          pr_count: number | null;
        }>).find((r) => r.or_count != null || r.pr_count != null);
        if (aHit) {
          if (!cancelled) {
            setOrCount(aHit.or_count ?? null);
            setPrCount(aHit.pr_count ?? null);
            setSource('assignment');
          }
          return;
        }
        // 2) Fallback — the New Account SOP captured OR/PR at onboarding even
        //    before a live assignment exists.
        const { data: sopRows, error: sErr } = await supabase
          .from('new_account_sop')
          .select('or_count, pr_count')
          .eq('facility_id', facility.facility_id)
          .limit(1);
        if (sErr) throw sErr;
        const sHit = ((sopRows ?? []) as Array<{
          or_count: number | null;
          pr_count: number | null;
        }>)[0];
        if (sHit && (sHit.or_count != null || sHit.pr_count != null)) {
          if (!cancelled) {
            setOrCount(sHit.or_count ?? null);
            setPrCount(sHit.pr_count ?? null);
            setSource('sop');
          }
        }
      } catch (e) {
        console.warn('[facility-detail] OR/PR lookup failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facility.facility_id]);

  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5">
      <div className="flex items-center gap-2">
        <Ruler className="h-4 w-4 text-tcs-text-secondary" />
        <h3 className="text-sm font-semibold text-tcs-text">Surgical rooms</h3>
      </div>
      {loading ? (
        <Skeleton width="50%" height={14} className="mt-2" block />
      ) : source ? (
        <div className="mt-3 flex flex-wrap items-end gap-8">
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
              OR count
            </dt>
            <dd className="mt-1 font-mono text-sm text-tcs-text">
              {orCount ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
              PR count
            </dt>
            <dd className="mt-1 font-mono text-sm text-tcs-text">
              {prCount ?? '—'}
            </dd>
          </div>
          <p className="text-[11px] text-tcs-text-muted">
            {source === 'assignment'
              ? 'From the active service assignment'
              : 'From the New Account SOP'}
          </p>
        </div>
      ) : (
        <p className="mt-2 text-[12px] italic text-tcs-text-muted">
          OR/PR not captured yet — add them on the New Account SOP, or in the
          Crew &amp; Sub block on the Info tab.
        </p>
      )}
    </div>
  );
}

// Floorplan — Phase 3 build (long-lead). Tagged in the README as a tab whose
// UI takes time to develop properly. Data model is in place; visual editor
// hasn't been started.
// 2026-05-21 — Status tab. Surfaces the AM-owned RAG: current status pill,
// category badge, reason text, "Last set by X on Y" attribution, a "Change
// RAG" button (opens RagEditModal), and the RAG history timeline (last 5
// from v_facility_rag_history, with "View all" expander). RAG is 100% AM
// judgment — inspection failures do NOT auto-change it. See app-readmes/
// account-management-README.md → "RAG editing" for the full spec.
function StatusTab({
  facility,
  onSaved,
}: {
  facility: Facility;
  onSaved: () => void;
}) {
  const { entries, loading, error, refresh } = useRagHistory(facility.facility_id);
  const [modalOpen, setModalOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const rag = facility.account_status_rag;
  const setByLabel = (() => {
    if (facility.rag_set_at) {
      const datePart = fmtDate(facility.rag_set_at);
      // For KPI-doc-backfilled rows, set_by is NULL on facilities; the
      // history view still shows them with source='KPI_Document_2026-05'.
      if (facility.rag_set_by) {
        return `Last set by ${facility.rag_set_by} on ${datePart}`;
      }
      return `Last set on ${datePart} (pre-launch import)`;
    }
    // 2026-07-27: the legacy rag_changed_date fallback is gone. Every row that
    // had a legacy date was backfilled into rag_set_at, so rag_set_at NULL now
    // genuinely means "RAG has never been set on this facility".
    return 'No attribution recorded';
  })();
  const visible = showAll ? entries : entries.slice(0, 5);

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-tcs-border bg-tcs-bg p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <span
                className="inline-flex h-12 w-12 items-center justify-center rounded-full text-sm font-semibold text-st-solid-ink"
                style={{
                  backgroundColor: rag ? RAG_COLORS[rag] : token('--st-neutral'),
                }}
                aria-label={rag ? `RAG: ${ragLabel[rag]}` : 'No RAG set'}
              >
                {rag ?? '—'}
              </span>
              <div>
                <h2 className="text-lg font-semibold text-tcs-text">
                  {rag ? ragLabel[rag] : 'No RAG set'}
                </h2>
                {facility.rag_category && (
                  <span className="mt-1 inline-flex items-center rounded-full bg-tcs-bg-muted px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-tcs-text-secondary">
                    {RAG_CATEGORY_LABELS[facility.rag_category]}
                  </span>
                )}
              </div>
            </div>
            {facility.rag_reason && (
              <p className="mt-3 max-w-2xl whitespace-pre-line text-sm text-tcs-text-secondary">
                {facility.rag_reason}
              </p>
            )}
            {(() => {
              // Onboarding-window indicator: surfaced when a facility was
              // auto-defaulted to Amber by the default_amber_on_activation
              // trigger and the 14-day onboarding window is still open.
              if (
                facility.status !== 'Active' ||
                facility.rag_category !== 'Operational' ||
                !facility.rag_set_at ||
                !facility.rag_reason ||
                !facility.rag_reason.toLowerCase().includes('default amber')
              ) return null;
              const setAt = new Date(facility.rag_set_at).getTime();
              const ageDays = (Date.now() - setAt) / (1000 * 60 * 60 * 24);
              if (ageDays > 14) return null;
              return (
                <p className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-st-warn-border-strong bg-st-warn-bg px-2 py-1 text-[11px] font-medium text-st-warn-fg-strong">
                  <span aria-hidden>⚠</span>
                  New account · Onboarding window · RAG set to Amber by default
                </p>
              );
            })()}
            <p className="mt-3 text-[11px] text-tcs-text-muted">{setByLabel}</p>
          </div>
          <Button size="sm" onClick={() => setModalOpen(true)}>
            Change RAG
          </Button>
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-tcs-text">RAG history</h3>
        <p className="mt-0.5 text-[11px] text-tcs-text-muted">
          AM-owned status changes over time. Entries tagged
          <span className="mx-1 inline-flex items-center rounded bg-tcs-bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tcs-text-secondary">
            imported
          </span>
          come from the pre-launch KPI document (May 2026 import).
        </p>
        <div className="mt-3 space-y-3">
          {loading && (
            <>
              <Skeleton width="100%" height={56} block />
              <Skeleton width="100%" height={56} block />
            </>
          )}
          {error && (
            <ErrorState
              title="Couldn't load history"
              body={error}
              onRetry={() => refresh()}
            />
          )}
          {!loading && !error && entries.length === 0 && (
            <EmptyState
              title="No history yet"
              body="When this facility's RAG changes, the entry will appear here."
            />
          )}
          {!loading && !error && visible.map((e, i) => (
            <RagHistoryRow key={`${e.set_at}-${i}`} entry={e} />
          ))}
          {!showAll && entries.length > 5 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="text-xs text-tcs-text-secondary underline-offset-2 hover:text-tcs-text hover:underline"
            >
              View all {entries.length} entries
            </button>
          )}
        </div>
      </section>

      <RagEditModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        facility={facility}
        onSaved={() => {
          onSaved();
          refresh();
        }}
      />
    </div>
  );
}

function RagHistoryRow({ entry }: { entry: FacilityRagHistoryEntry }) {
  const status = entry.rag_status_new;
  const isImported = entry.source !== 'live';
  return (
    <div className="flex items-start gap-3 rounded-lg border border-tcs-border bg-tcs-bg p-3">
      <span
        className="mt-1 inline-block h-3 w-3 shrink-0 rounded-full"
        style={{
          backgroundColor: status ? RAG_COLORS[status] : token('--st-neutral'),
        }}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="font-medium text-tcs-text">
            {entry.rag_status_old
              ? `${entry.rag_status_old} → ${status ?? '—'}`
              : status ?? '—'}
          </span>
          {entry.rag_category && (
            <span className="inline-flex items-center rounded bg-tcs-bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tcs-text-secondary">
              {RAG_CATEGORY_LABELS[entry.rag_category]}
            </span>
          )}
          {isImported && (
            <span
              className="inline-flex items-center rounded bg-tcs-bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tcs-text-muted"
              title={`Imported from ${entry.source}`}
            >
              imported
            </span>
          )}
        </div>
        {entry.rag_reason && (
          <p className="mt-1 line-clamp-3 text-[12px] text-tcs-text-secondary">
            {entry.rag_reason}
          </p>
        )}
        <p className="mt-1 text-[11px] text-tcs-text-muted">
          by {entry.set_by_name ?? entry.set_by_employee_id ?? 'unattributed'} on{' '}
          {fmtDate(entry.set_at)}
        </p>
      </div>
    </div>
  );
}

function FloorplanTab() {
  return (
    <InDevelopmentPanel
      icon={<LayoutGrid className="h-6 w-6" />}
      title="Floorplan"
      phase="Phase 3 — long-build"
      body={
        <>
          Building layout sketch, floor types per area (tile / VCT / carpet),
          closet locations, logbook locations. Distinct from the global
          Facilities Map (which lives in top-level nav).
        </>
      }
    />
  );
}

// Supplies — two sections at this building:
//   * Chemicals on site — added, edited and removed HERE, by anyone at TCS, at
//     any time. <FacilityChemicalsPanel> owns it.
//   * Tracked supplies (from facility_supply_allocation), grouped by category,
//     still read-only and still edited in the Supply Management module.
//
// 2026-09-09, facility-chemicals-in-app. What used to sit here said this card
// took no writes and that the survey was where chemicals came from. That was a
// note from a 2026-05-19 run, not a design principle, and Ben overrode it: the
// survey is dead, no chemical had been recorded anywhere since 2026-05-27, and
// 190 active buildings had none. It is deleted, along with the two strings
// under it that told people to go and use the survey.

interface FacilitySupplyAllocationDisplayRow {
  allocation_id: string;
  supply_id: string | null;
  supply_name: string | null;
  supply_category: string | null;
  par_level: number | null;
  current_qty: number | null;
  last_restocked: string | null;
  status: string | null;
}

function SuppliesTab({ facility_id }: { facility_id: string }) {
  const [allocations, setAllocations] = useState<FacilitySupplyAllocationDisplayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const allocRes = await supabase
          .from('facility_supply_allocation')
          .select(
            'allocation_id, supply_id, supply_name, supply_category, par_level, current_qty, last_restocked, status',
          )
          .eq('facility_id', facility_id);
        if (cancelled) return;
        if (allocRes.error) throw allocRes.error;
        setAllocations(
          (allocRes.data ?? []) as FacilitySupplyAllocationDisplayRow[],
        );
      } catch (e) {
        if (cancelled) return;
        console.error('[supplies-tab] load failed', e);
        setError(errorMessage(e, 'Failed to load supplies.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [facility_id]);

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton width="100%" height={80} block />
        <Skeleton width="100%" height={120} block />
      </div>
    );
  }
  if (error) {
    return (
      <ErrorState title="Couldn't load supplies" body={error} onRetry={() => setLoading(true)} />
    );
  }

  // ⛔ No early return on an empty building any more. The chemicals section is
  // where chemicals are ADDED, so it has to render on a building that has none
  // — which is all 190 of them.

  // Group allocations by category for the second section; NULL → "Other".
  const allocationGroups = new Map<string, FacilitySupplyAllocationDisplayRow[]>();
  for (const a of allocations) {
    const key = a.supply_category ?? 'Other';
    const arr = allocationGroups.get(key) ?? [];
    arr.push(a);
    allocationGroups.set(key, arr);
  }
  const groupKeys = Array.from(allocationGroups.keys()).sort();

  return (
    <div className="space-y-4">
      {/* 2026-09-23 — dialysis chemical stock. Renders only for an in-scope
          dialysis clinic (a card), a customer-supplied one (one line), and
          nothing for any other building. */}
      <DialysisStockCard facilityId={facility_id} />
      <FacilityChemicalsPanel facility_id={facility_id} />

      {allocations.length === 0 && (
        <EmptyState
          title="No tracked supplies at this facility yet."
          body="Usage and allocations are tracked in the Supply Management module."
          cta={
            <Button as={Link} to="/supply" variant="secondary" size="sm" icon={<Package className="h-4 w-4" />}>
              Open Supply Management
            </Button>
          }
        />
      )}

      {allocations.length > 0 && (
        <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5">
          <h3 className="text-sm font-semibold text-tcs-text">
            Tracked supplies
            <span className="ml-2 text-xs font-normal text-tcs-text-muted">
              ({allocations.length})
            </span>
          </h3>
          <div className="mt-3 space-y-3">
            {groupKeys.map((key) => (
              <div key={key}>
                <h4 className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
                  {key}
                </h4>
                <ul className="mt-1 space-y-1 text-sm">
                  {allocationGroups.get(key)!.map((a) => (
                    <li key={a.allocation_id} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                      <span className="text-tcs-text">
                        {a.supply_name ?? a.supply_id ?? a.allocation_id}
                      </span>
                      <span className="text-xs text-tcs-text-muted">
                        qty: {a.current_qty ?? '—'}
                        {a.par_level != null ? ` · par: ${a.par_level}` : ''}
                        {a.last_restocked ? ` · last restocked: ${a.last_restocked}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-tcs-text-muted">
            Read-only. Edit allocations and request restocks in Supply Management.
          </p>
        </div>
      )}
    </div>
  );
}

// Actions — Phase 2 build. Quick-action buttons that mirror what's available
// via AI Chat. Per the design principle: AI Chat is additive, every action
// is also available via button. Specifics TBD with Jack.
function ActionsTab({ facility }: { facility: Facility }) {
  return (
    <div className="space-y-4">
      {/* Stop Service — the facility-scoped lifecycle action, and the only
          lifecycle thing in this tab. It LINKS to /stop-service rather than
          embedding the form: the Info tab links to the same route, and two
          copies of a form that pauses a client is how they drift apart.

          2026-08-21, Ben: "Stop Service replaced account termination, so
          remove the account termination portion in the Actions tab." The
          <AccountTerminationPanel> that used to sit below this is gone from
          here — but NOT deleted. Five controls only it had (Finance ack, HR
          ack, exec approval, set the final service date, cancel) now render
          inside the Stop Service route in manage-only mode, so the ack chain
          still has a home and the ack notification still has somewhere to
          land. See the manageOnly prop for the full inventory. */}
      <StopServiceEntry facility={facility} />
      <InDevelopmentPanel
        icon={<Zap className="h-6 w-6" />}
        title="More actions"
        phase="Phase 2 — coming soon"
        body={
          <>
            Quick-action buttons for the most common AM workflows at this
            facility. Every action available here will also be available via
            AI Chat — buttons aren&rsquo;t required, just faster.
          </>
        }
      />
    </div>
  );
}

/**
 * The Stop Service entry point.
 *
 * Rendered in TWO places — the Actions tab and the bottom of the Info tab —
 * and it is the same component both times, linking to the same route. Ben's
 * instruction, 2026-08-20: "not two identical pages. Just one page that gets
 * linked to it from another location."
 *
 * The wording carries the distinction that has cost us facilities: this stops
 * service at ONE SITE. Terminating the account is a different, rarer act, and
 * it lives in the panel below this one in the Actions tab.
 */
function StopServiceEntry({ facility }: { facility: Facility }) {
  // Same derivation the Stop Service page uses, so the card and the page can
  // never disagree about what this facility's state is. Role flags are passed
  // true and the open-termination id null on purpose: this card describes the
  // FACILITY's state, not what the current user may do about it — the page
  // itself gates the controls, and a card that hides itself by role would just
  // leave the AM with no explanation of why there is nothing here.
  const { isPaused, isRecordClosed } = deriveStopServiceCapabilities({
    status: facility.status,
    serviceState: facility.service_state,
    rolePause: true,
    roleTerminate: true,
    openTerminationId: null,
  });

  // The planned last service date lives on account_terminations, not on the
  // facility, so the card fetches it. One row, and only when there is
  // something dated to show — a facility in normal service needs no query.
  // Ben, 2026-08-21: Finance needs planned AND actual, for planning and for
  // paying the sub respectively.
  const [termDates, setTermDates] = useState<{
    final_service_date: string | null;
    status: string | null;
  } | null>(null);
  const needsTermDates = isPaused || isRecordClosed || facility.service_state === 'Ended';
  const accountId = facility.account_id ?? null;
  const thisFacilityId = facility.facility_id;

  useEffect(() => {
    if (!needsTermDates || !accountId) {
      setTermDates(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      // Only a termination COVERING THIS FACILITY (named, or account-wide).
      // An account can carry one per facility since 2026-10-13, and "the
      // account's latest" put a sibling's date on this card.
      const { data } = await supabase
        .from('account_terminations')
        .select('final_service_date, status')
        .eq('account_id', accountId)
        .or(coversFacilityFilter(thisFacilityId))
        .not('status', 'in', '("Cancelled")')
        .order('created_at', { ascending: false })
        .limit(1);
      if (cancelled) return;
      setTermDates(
        ((data ?? []) as Array<{ final_service_date: string | null; status: string | null }>)[0] ??
          null,
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [needsTermDates, accountId, thisFacilityId]);

  const dateRows = lifecycleDateRows({
    serviceState: facility.service_state,
    status: facility.status,
    serviceStateChangedAt: facility.service_state_changed_at,
    expectedResumeDate: facility.expected_resume_date,
    closedDate: facility.closed_date,
    terminationFinalServiceDate: termDates?.final_service_date ?? null,
    terminationStatus: termDates?.status ?? null,
  });

  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-tcs-text">
            <PauseCircle className="h-4 w-4 text-tcs-text-secondary" />
            Stop service at {facility.facility_name}
            <ServiceStateChip state={facility.service_state} size="sm" />
          </h3>
          <p className="mt-1 max-w-prose text-xs text-tcs-text-secondary">
            {isRecordClosed
              ? `This facility is ${facility.status}. There is nothing left to pause or terminate.`
              : isPaused
                ? 'Service is paused — the rate, the crew assignment and the relationship are all being held. Open this to resume service, or to end it for good.'
                : 'Pause it (temporary — the rate, the crew and the relationship survive) or terminate it (service ends for good, on a final service date, after Finance, HR and an executive sign off). This affects this facility only, never the whole account.'}
          </p>
        </div>
        <Button
          size="sm"
          variant={isPaused ? 'primary' : 'secondary'}
          as={Link}
          to={`/account-management/facilities/${facility.facility_id}/stop-service`}
          icon={<PauseCircle className="h-4 w-4" />}
        >
          {isRecordClosed
            ? 'View service state'
            : isPaused
              ? 'Resume or end service'
              : 'Stop service'}
        </Button>
      </div>

      {/* Planned vs actual. Finance reads the planned date to forecast the
          month a facility leaves the book, and the actual date to pay the sub
          for the days they really worked. One column cannot answer both. */}
      {dateRows.length > 0 && (
        <LifecycleDates rows={dateRows} className="mt-3 border-t border-tcs-border pt-3" />
      )}
    </div>
  );
}

// Info — Phase 2 build. Per Jack: alarm code + arm/disarm procedure + lock
// type + badge requirements + key location, plus the Site Cheat Sheet
// (special-instruction zones, restroom locations, notable contacts), plus
// square footage with flag-for-review and an Open in Maps button.
//
// Two render paths controlled by INFO_TAB_PHASE2:
//   * false (default today) — existing UI: InDevelopmentPanel + address card
//     with Open in Maps. Behavior unchanged from Phase 1 ship.
//   * true (post-migration apply) — fetches facility_access row + total_sqft
//     from the right facility_details_* table; renders Security/Access,
//     Site Cheat Sheet, Square Footage, and the Open in Maps card.
//
// The "Open in Maps" button works today against `facility_address` and is
// shown in both modes.
function InfoTab({
  facility,
  onFacilityChange,
}: {
  facility: Facility;
  onFacilityChange: () => void;
}) {
  const addr = facility.facility_address;
  // CW-C Phase 1 (2026-08-04): this used to build its own
  // `https://maps.apple.com/?q=<address>` link — which is a Maps *search*, not
  // a directions link, and hard-coded Apple while the map sheet hard-coded
  // Google + the stored coordinate. That search URL is what drove Ben 721 m
  // wrong at Old Tesson. Both now go through <NavigateButton>.
  // CW-C Phase 4 (2026-08-05): coordinate-FIRST when the coordinate has ROOFTOP
  // provenance; address only as the fallback guess. `coordTrusted` must be
  // derived by isCoordTrusted — never hand-set to true.
  const navTarget: NavTarget = {
    address: addr,
    lat: facility.latitude,
    lng: facility.longitude,
    coordTrusted: isCoordTrusted(
      facility.geocode_precision,
      facility.coord_verified_result,
    ),
    name: facility.facility_name,
  };

  // Phase 2 path — feature-flagged off until the migration applies.
  if (INFO_TAB_PHASE2) {
    return (
      <InfoTabPhase2
        facility={facility}
        navTarget={navTarget}
        onFacilityChange={onFacilityChange}
      />
    );
  }

  // Phase 1 path — current production UI.
  return (
    <div className="space-y-4">
      <InDevelopmentPanel
        icon={<Info className="h-6 w-6" />}
        title="Info"
        phase="Phase 2 — coming soon"
        body={
          <>
            Security &amp; access (alarm code, arm/disarm procedure, lock
            type, badge requirements, key location). Site Cheat Sheet
            (special-instruction zones, restroom locations, notable contacts).
            Square footage with flag-for-review. Administrative metadata.
          </>
        }
      />
      <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5">
        <h3 className="text-sm font-semibold text-tcs-text">Address</h3>
        <p className="mt-2 text-sm text-tcs-text-secondary">{addr ?? '—'}</p>
        <div className="mt-3">
          <NavigateButton target={navTarget} variant="link" />
        </div>
        {/* Field verification sits next to Navigate on purpose: the moment you
            have just used the pin is the only moment you know if it worked. */}
        {(facility.latitude != null && facility.longitude != null) && (
          <div className="mt-3 border-t border-tcs-border pt-3">
            <CoordVerifyControl
              facilityId={facility.facility_id}
              current={facility.coord_verified_result ?? null}
              verifiedAt={facility.coord_verified_at ?? null}
              onReported={onFacilityChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ----- Info tab — Phase 2 implementation ----------------------------------
// Reachable only when INFO_TAB_PHASE2 is true. All queries below assume the
// migration `20260510_facility_access.sql` has been applied. If the table is
// missing, the queries will return PostgREST 42P01 errors — render an
// ErrorState rather than crashing.

// ============================================================================
// Info tab — Phase 2 editable implementation (2026-05-16)
//
// This block replaces the previous read-only InfoTabPhase2 + its sister
// cards. The new flow:
//   - All 5 tables load in parallel via useFacilityInfoData; one query
//     failing never blocks the rest of the page rendering.
//   - Direct fields use InlineEditField (click → edit → blur/Enter saves).
//   - Rate fields render read-only with a "Change the rate" button that opens
//     RateChangeModal. ⛔ That modal calls record_rate_change(), the ONE
//     function that writes a rate (TCSD-0188). Nothing here writes the column.
//   - service_assignments.provider_id is read-only with a "Change sub"
//     button that navigates to /sub-replacement/new.
//   - Sub-agreement URLs keep the existing ContractRow pattern.
//
// Defensive load contract: NEVER renders the legacy "Couldn't load Info"
// ErrorState. Per-section error banners surface if any query failed,
// but the rest of the page still renders.
// ============================================================================

function InfoTabPhase2({
  facility,
  navTarget,
  onFacilityChange,
}: {
  facility: Facility;
  navTarget: NavTarget;
  onFacilityChange: () => void;
}) {
  const info = useFacilityInfoData(facility);
  const { update, updateFields } = useFacilityFieldUpdate();

  // Belt-and-suspenders to the chat-track seed (Task 2, 2026-07-14): if the
  // type-detail row is missing, create an empty one so the detail tab is
  // editable without a manual seed (fixes the Davita Boulder "ask Ben to seed"
  // wall). Runs once per facility; the open_edit_all_employees INSERT policy
  // permits it. saveDetailsField still has its own UPDATE-then-INSERT path.
  // 2026-09-17 — no longer gated on the building having a detail table, because
  // every building has one. A facility_type of 'Other' used to reach this with
  // no table and get no row; it gets a row now like everything else.
  const seedAttemptedRef = useRef<string | null>(null);
  useEffect(() => {
    if (info.loading) return;
    if (info.details) return;
    if (seedAttemptedRef.current === facility.facility_id) return;
    seedAttemptedRef.current = facility.facility_id;
    void (async () => {
      const { error } = await supabase
        .from('facility_details')
        .insert({ facility_id: facility.facility_id });
      if (!error) info.refresh();
      else console.warn('[facility-detail] auto-seed details row failed', error.message);
    })();
  }, [info.loading, info.details, facility.facility_id, info]);

  // Local optimistic state for facility_access INSERT-on-first-save: when
  // the row doesn't exist yet, the first save creates it and we want the
  // returned access_id so subsequent saves switch to UPDATE.
  const [accessIdOverride, setAccessIdOverride] = useState<string | null>(null);
  const effectiveAccessId = info.access?.access_id ?? accessIdOverride;

  // Open proposals for this facility (just-pending; ignore Approved/Rejected
  // for the badge). Loaded lazily after the main data lands.
  const [openProposals, setOpenProposals] = useState<
    Array<{
      proposal_id: string;
      target_table: string;
      target_row_id: string | null;
      field_name: string;
    }>
  >([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error } = await supabase
        .from('rate_change_proposals')
        .select('proposal_id, target_table, target_row_id, field_name, status')
        .eq('facility_id', facility.facility_id)
        .eq('status', 'Pending');
      if (cancelled) return;
      if (error) {
        // Non-fatal — Open Proposals badge just won't show.
        console.warn('[info-tab] open-proposals lookup failed', error);
        return;
      }
      setOpenProposals(
        (data ?? []).map((p: any) => ({
          proposal_id: p.proposal_id,
          target_table: p.target_table,
          target_row_id: p.target_row_id,
          field_name: p.field_name,
        })),
      );
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [facility.facility_id, info.loading]);

  function hasProposalFor(
    target_table: string,
    target_row_id: string | null,
    field_name: string,
  ): boolean {
    return openProposals.some(
      (p) =>
        p.target_table === target_table &&
        p.field_name === field_name &&
        (p.target_row_id ?? null) === (target_row_id ?? null),
    );
  }

  // The facility_access card's ONE save — handles INSERT-on-first-save, and
  // writes every changed field in a single statement.
  async function saveAccessFields(patch: Record<string, string | number | null>) {
    if (Object.keys(patch).length === 0) return { ok: true, error: null };
    const result = await updateFields({
      table: 'facility_access',
      row_id: effectiveAccessId ?? null,
      pk_column: 'access_id',
      fields: patch,
      label: 'save the security and access details',
      facility_id: facility.facility_id,
    });
    if (result.ok && result.new_row_id) {
      setAccessIdOverride(result.new_row_id);
      // Pull the fresh row so subsequent renders show all server-side defaults.
      info.refresh();
    }
    return { ok: result.ok, error: result.error };
  }

  // 2026-06-15 (Jack: "doesn't save first try, saves on refresh") — idempotent
  // save against public.facility_details, keyed on facility_id. (Until
  // 2026-09-17 the first job of this function was picking WHICH of three tables
  // to write to, from the building's type. There is one table now.)
  // Every building has a backfilled detail row, so in practice this is always
  // an UPDATE — which is the only write an AM's RLS (am_update_my_facility)
  // permits. We deliberately do NOT use a blind PostgREST upsert: that issues
  // INSERT…ON CONFLICT, and AMs have no INSERT policy on facility_details
  // (only superuser), so it would 42501 even when the row exists. Instead we
  // UPDATE first and only INSERT if no row matched (superuser path). After a
  // successful write we refetch the detail row so the saved value renders
  // immediately — the missing refetch was the desync.
  async function saveDetailsFields(patch: Record<string, string | number | null>) {
    if (Object.keys(patch).length === 0) return { ok: true, error: null };
    // The save itself is lib/facility-details.ts (UPDATE first, INSERT only when
    // no row matched) — the door Ask AI's building card uses too.
    const res = await saveFacilityDetailsFields(facility.facility_id, patch);
    if (!res.ok) return res;

    // Invalidate/refetch so the saved value renders without a manual refresh.
    info.refresh();
    return { ok: true, error: null };
  }

  // 2026-08-13 (CW-O PR3) — the multi-column form is the primitive, because the
  // sterile split must land in ONE statement: non_sterile_sqft is derived from
  // total_sqft − sterile_sqft, and a half-applied pair leaves a row that no
  // longer ties. One UPDATE also means one audit_log row whose changed_fields
  // names every column together.
  //
  // 2026-09-16 — the single-field wrapper that sat here is gone with the last
  // per-field save on this tab. Every card writes a patch.

  async function saveAssignmentField(
    assignmentId: string,
    field: string,
    value: string | number | boolean | null,
  ) {
    return update({
      table: 'service_assignments',
      row_id: assignmentId,
      pk_column: 'assignment_id',
      field,
      value,
      // The one door needs to know which building's line this is; it refuses a
      // line that is not on the building it was given.
      facility_id: facility.facility_id,
    });
  }

  // A whole service-line window in ONE call to save_service_line(). It applies
  // what it accepts and names what it refuses per field, so the window can say
  // exactly which values it kept.
  async function saveAssignmentFields(
    assignmentId: string,
    fields: Record<string, string | number | boolean | null>,
  ) {
    if (Object.keys(fields).length === 0) return { ok: true, error: null };
    const res = await updateFields({
      table: 'service_assignments',
      row_id: assignmentId,
      pk_column: 'assignment_id',
      fields,
      label: 'save this service line',
      facility_id: facility.facility_id,
    });
    return { ok: res.ok, error: res.error };
  }

  // ⛔ THE ROUTES IN "Changed somewhere else" GO TO REAL SURFACES ONLY.
  // The building's rate is the SUM of its service lines and is changed on a
  // line, so "where the rate is changed" is the Crew & Sub cards on this same
  // page — a real destination — not an invented facility-level rate screen.
  // A row whose owning screen does not exist renders with no button at all.
  const scrollToCard = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const goToServiceLines = useCallback(
    () => scrollToCard('info-card-crew-and-sub'),
    [scrollToCard],
  );
  const goToAccountSummary = useCallback(
    () => scrollToCard('info-card-account-summary'),
    [scrollToCard],
  );

  if (info.loading) {
    return (
      <div className="space-y-3">
        <Skeleton width="100%" height={120} block />
        <Skeleton width="100%" height={80} block />
        <Skeleton width="100%" height={80} block />
      </div>
    );
  }

  return (
    <FacilityNaProvider naFieldPaths={info.naFieldPaths} loaded={!info.loading}>
    <div className="space-y-4">
      <AccountSummarySection
        facility={facility}
        navTarget={navTarget}
        onFacilityChange={onFacilityChange}
        onGoToServiceLines={goToServiceLines}
      />

      {info.errors.contacts && (
        <SectionErrorBanner section="Contacts" message={info.errors.contacts} />
      )}
      <PointsOfContactCard
        contacts={info.contacts}
        facility_id={facility.facility_id}
        account_id={facility.account_id ?? null}
        onRefresh={info.refresh}
        onGoToServiceLines={goToServiceLines}
      />

      {info.errors.access && (
        <SectionErrorBanner section="Facility Access" message={info.errors.access} />
      )}
      <FacilityAccessCard
        access={info.access}
        accessIdOverride={accessIdOverride}
        onSaveFields={saveAccessFields}
        onGoToServiceLines={goToServiceLines}
      />

      {info.errors.details && (
        <SectionErrorBanner section="Facility Details" message={info.errors.details} />
      )}
      <FacilityDetailsCard
        details={info.details}
        facility_type={facility.facility_type ?? null}
        onSaveFields={saveDetailsFields}
        onGoToAccountSummary={goToAccountSummary}
      />

      {info.errors.assignments && (
        <SectionErrorBanner
          section="Crew & Sub"
          message={info.errors.assignments}
        />
      )}
      <CrewAndSubCard
        assignments={info.assignments}
        facility_id={facility.facility_id}
        facilityStatus={facility.status ?? null}
        // Item 904: the one place a pause started — the building's own
        // timestamp, not a copy on the line. Only meaningful while paused.
        pausedSince={
          facility.service_state === 'Suspended' ? (facility.service_state_changed_at ?? null) : null
        }
        hasProposalFor={hasProposalFor}
        onSaveAssignment={saveAssignmentField}
        onSaveAssignmentFields={saveAssignmentFields}
        onRefresh={info.refresh}
        // Not started / start date move a line in or out of the building's
        // rate, so the building is re-read with the lines (no stale total).
        onLineLifeChanged={() => {
          info.refresh();
          onFacilityChange();
        }}
      />

      {/* Item 867: a hospital's own billing lines, named the way the hospital
          names them (TCSD-0253), with what each billed. Hospitals only — the
          demo Ben approved is the hospital ledger. */}
      {facility.facility_type === 'Hospital' && (
        <HospitalLinesCard facilityId={facility.facility_id} />
      )}

      {/* EVSiQ (2026-10-27, write path A): an account manager enters a room's cleaning log from
          what the cleaner sent. Renders only on a building with rooms on EVSiQ, for field seats. */}
      <EvsiqCleaningLogCard facilityId={facility.facility_id} buildingLabel={facility.facility_name ?? facility.facility_id} />
      <EvsiqSurveyorLinksCard facilityId={facility.facility_id} buildingLabel={facility.facility_name ?? facility.facility_id} />
      {/* Punch kiosk photos on/off — any full TCS employee (Ben, 1 Nov 2026). */}
      <KioskPhotosCard facilityId={facility.facility_id} />

      <FinancialSummaryCard
        facility={facility}
        assignments={info.assignments}
        margin={info.margin}
        cluster={info.cluster}
        onRefresh={info.refresh}
        onGoToServiceLines={goToServiceLines}
      />

      {/* Item 653: the building's client documents. They used to live only on the
          onboarding card, which locks when it closes. */}
      <FacilityDocumentsCard
        facilityId={facility.facility_id}
        accountId={facility.account_id ?? null}
        canAdd={facility.status !== 'Closed'}
      />

      {/* Stop Service, last — Ben: "a 'stop service' button in the account
          information tab (where everything else that actually gets used
          exists) near the bottom somewhere as well". Same component and same
          destination as the Actions tab entry: one page, two doors. It sits
          after the money because stopping service is the thing you do having
          read everything above it. */}
      <StopServiceEntry facility={facility} />
    </div>
    </FacilityNaProvider>
  );
}

// ============================================================================
// Section: Account Summary (read-only)
// ============================================================================
// (AM validation layer removed 2026-07-06 per Ben — too much friction.)

// AFB-0052 — soft, non-blocking brand-naming convention hint for facility
// renames. Returns a hint when the name looks like a known brand but breaks
// the house convention, else null. NEVER blocks the save.
function brandNameHint(raw: string | null | undefined): string | null {
  const name = (raw ?? '').trim();
  if (!name) return null;
  if (/^davita\b/i.test(name) && !/^DaVita - .+/.test(name)) {
    return 'Heads up: DaVita facilities are usually named “DaVita - [Clinic]”.';
  }
  if (/^fresenius\b/i.test(name) && !/^Fresenius - .+/.test(name)) {
    return 'Heads up: Fresenius facilities are usually named “Fresenius - [Clinic]”.';
  }
  if (/baylor|scott\s*&?\s*white/i.test(name) && !/BS&W/.test(name)) {
    return 'Heads up: Baylor Scott & White facilities usually use the “BS&W” prefix.';
  }
  return null;
}

// ============================================================================
// Account Summary — ONE Edit button, ONE window, ONE Save.
//
// 2026-09-16 (info-tab-one-edit-door). This card used to carry four per-field
// pencils — name, market, account manager, usables — that each saved on their
// own the moment you tabbed out, and the other eight fields could not be
// changed here at all. It also hid those four pencils behind a role list
// (superuser / executive / finance / national_am), so a business developer
// opened the building they had just sold and found nothing they could correct.
//
// ⛔ THE ROLE LISTS ARE STRUCK. TCSD-0170 settled this on 2026-09-02 for
// projects and their service lines — "All can edit. Leave it that way. Audit
// trails will find changes to money." The Info tab was simply never swept. The
// database already permitted every one of these writes: open_edit_all_employees
// (FOR ALL USING true) sits on facilities, so the role list was a UI-only
// restriction with nothing behind it, and audit_trigger on facilities already
// records who changed what, from what, to what, and when.
//
// ⛔ canEditAm STAYS. It is not a UI-only restriction: assign_facility_am()
// enforces its own rule server-side and this gate mirrors it, so widening the
// gate would only render a control that 42501s.
// ============================================================================

function AccountSummarySection({
  facility,
  navTarget,
  onFacilityChange,
  onGoToServiceLines,
}: {
  facility: Facility;
  navTarget: NavTarget;
  onFacilityChange: () => void;
  /** Scrolls to the Crew & Sub cards — where the rate is actually changed. */
  onGoToServiceLines: () => void;
}) {
  const { profile, employee } = useAuth();
  const { updateFields } = useFacilityFieldUpdate();
  const { markets, error: marketsError } = useMarkets();
  const { employees: amEmployees, error: amEmployeesError } = useActiveEmployees(true);
  const { bdds, error: bddError } = useBddOptions(true);

  const [mode, setMode] = useState<CardMode>('view');
  const [draft, setDraft] = useState<Record<string, string | number | null>>({});
  const hasChanges = Object.keys(draft).length > 0;

  // The AM gate mirrors assign_facility_am()'s own rule — see the header.
  const role = profile?.role;
  const fullAmAuthority = hasFullAmAuthority(role);
  const facilityFinalised = facility.status === 'Active';
  const isActiveEmployee = employee?.employment_status === 'Active';
  const canEditAm = fullAmAuthority || (!facilityFinalised && isActiveEmployee);

  const stored: Record<string, string | number | null> = {
    facility_name: facility.facility_name ?? null,
    facility_address: facility.facility_address ?? null,
    market_region: facility.market_region ?? null,
    facility_type: facility.facility_type ?? null,
    sold_by_eid: facility.sold_by_eid ?? null,
    tcs_provides_usables: toUsablesChoice(facility.tcs_provides_usables),
    account_manager_eid: facility.account_manager_eid ?? null,
  };

  function getValue(key: string): string | number | null {
    return key in draft ? draft[key] : stored[key];
  }

  function setDraftField(key: string, next: string | number | null) {
    setDraft((prev) => {
      const normalised = next === '' ? null : next;
      if ((stored[key] ?? null) === normalised) {
        const { [key]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: normalised };
    });
  }

  // --- facility type --------------------------------------------------------
  // The picker offers all five canonical types on purpose: a person correcting
  // a wrong type must be able to SEE the type they believe it is and be told
  // why this screen cannot make that particular change, rather than find the
  // option silently missing and conclude the app is broken.
  const draftedType = String(getValue('facility_type') ?? '');
  const typeRefusal =
    'facility_type' in draft ? refusalForTypeChange(facility.facility_type, draftedType) : null;

  const nameHint = brandNameHint(String(getValue('facility_name') ?? ''));

  const soldByName =
    facility.sold_by_name ??
    bdds.find((b) => b.employee_id === facility.sold_by_eid)?.name ??
    facility.sold_by_eid ??
    null;
  // A recorded seller who is not BDD-flagged is still shown — pickers exclude,
  // resolvers resolve (TCSD-0129). Offer them as the current value so opening
  // the window cannot silently blank the fact.
  const soldByOptions = [
    ...bdds,
    ...(facility.sold_by_eid && !bdds.some((b) => b.employee_id === facility.sold_by_eid)
      ? [{ employee_id: facility.sold_by_eid, name: soldByName ?? facility.sold_by_eid }]
      : []),
  ];

  async function handleCardSave(): Promise<{ ok: boolean; error: string | null }> {
    // Refuse BEFORE any write. A cross-table type change must never land half
    // applied, and the person must read why (PLAYBOOK §10d — prove the refusal
    // by reading it fire, not by reasoning about it).
    if (typeRefusal) return { ok: false, error: typeRefusal };

    const name = getValue('facility_name');
    if ('facility_name' in draft && (name == null || String(name).trim() === '')) {
      return { ok: false, error: 'Facility name is required.' };
    }

    // ONE statement for every plain column on facilities. One round trip, one
    // audit_log row naming all of them together, and no way to half-apply.
    // (summaryColumnsPatch: the same trimming Ask AI's building card uses.)
    const patch: Record<string, string | number | boolean | null> = summaryColumnsPatch(draft);
    if ('sold_by_eid' in draft) {
      patch.sold_by_eid = (draft.sold_by_eid as string | null) || null;
    }
    if ('tcs_provides_usables' in draft) {
      patch.tcs_provides_usables = fromUsablesChoice(String(draft.tcs_provides_usables ?? ''));
    }

    if (Object.keys(patch).length > 0) {
      const res = await updateFields({
        table: 'facilities',
        row_id: facility.facility_id,
        pk_column: 'facility_id',
        fields: patch,
        label: 'save this building’s details',
      });
      if (!res.ok) return { ok: false, error: res.error };
    }

    // ⛔ The account manager is a SEPARATE declared write because it is a
    // separate rule: assign_facility_am() sets the protected-column bypass and
    // authorises itself. It is not a column this card may update.
    if ('account_manager_eid' in draft) {
      const nextEid = draft.account_manager_eid ? String(draft.account_manager_eid) : null;
      if (!nextEid) {
        return {
          ok: false,
          error:
            'An account manager is required — pick a person, or press Cancel to leave it as it was.',
        };
      }
      const { error: rpcError } = await assignFacilityAm(facility.facility_id, nextEid);
      if (rpcError) return { ok: false, error: rpcError.message };
      refreshReaders(['facilities']);
    }

    setDraft({});
    onFacilityChange();
    return { ok: true, error: null };
  }

  const elsewhere: ElsewhereRow[] = [
    {
      label: 'Status',
      value: facility.status ?? '(not set)',
      ...(facility.source_sop_id
        ? {
            go: {
              label: 'Open the onboarding card',
              onGo: () => {
                window.location.assign(`/new-accounts?card=${facility.source_sop_id}`);
              },
            },
          }
        : {
            note: 'This building predates the onboarding card, so there is no card to open. Status changes run through the building’s lifecycle — pause, resume or stop service.',
          }),
    },
    {
      label: 'Monthly rate',
      value:
        facility.monthly_rate == null
          ? 'No rate — this building has no priced service line'
          : `$${Number(facility.monthly_rate).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} / month`,
      note: 'Nobody types this. It is the sum of this building’s running service lines, and it changes when one of those lines changes.',
      go: { label: 'Go to the service lines', onGo: onGoToServiceLines },
    },
    {
      label: 'Account',
      value: facility.account_id ?? '(not set)',
      // ⛔ NO BUTTON. There is no account page in this app today —
      // /account-management is the list of buildings, not a record for ACT-####.
      // A button here would land somewhere that is not the account.
      note: 'There is no account screen to open yet. Which account a building belongs to is set when the account is created.',
    },
    { label: 'Facility ID', value: facility.facility_id, never: true },
    {
      label: 'State',
      value: facility.state ?? '(not set)',
      note: 'Worked out from the address above — correcting the address corrects the state.',
      never: true,
    },
  ];

  const summaryBody = (bodyMode: CardMode) =>
    bodyMode === 'edit' ? (
      <dl className="grid grid-cols-1 gap-4">
        <div>
          <InlineEditField
            label="Facility name"
            value={getValue('facility_name')}
            onChange={(v) => setDraftField('facility_name', v)}
            fieldKey="facility_name"
            displayMode="edit"
            full
          />
          {nameHint && <p className="mt-1 text-xs text-st-warn-fg">{nameHint}</p>}
        </div>
        <div>
          <InlineEditField
            label="Address"
            value={getValue('facility_address')}
            onChange={(v) => setDraftField('facility_address', v)}
            type="multiline"
            rows={2}
            fieldKey="facility_address"
            displayMode="edit"
            full
          />
          <p className="mt-1 text-xs text-tcs-text-muted">
            State is worked out from the address, so correcting the address corrects the state.
          </p>
        </div>
        <div>
          <InlineEditField
            label="Market"
            value={getValue('market_region')}
            onChange={(v) => setDraftField('market_region', v == null ? null : String(v))}
            type="select"
            options={marketOptions(markets).map((m) => ({ value: m, label: m }))}
            fieldKey="market_region"
            displayMode="edit"
          />
          {marketsError && (
            <p className="mt-1 text-xs text-st-warn-fg">
              The market list could not be loaded, so this picker is empty. Reload before
              changing it.
            </p>
          )}
        </div>
        {canEditAm ? (
          <div>
            <InlineEditField
              label="Account manager"
              value={getValue('account_manager_eid')}
              onChange={(v) =>
                setDraftField('account_manager_eid', v == null ? null : String(v))
              }
              type="select"
              options={amEmployees.map((e) => ({ value: e.employee_id, label: e.name }))}
              fieldKey="account_manager_eid"
              displayMode="edit"
            />
            {amEmployeesError && (
              <p className="mt-1 text-xs text-st-warn-fg">
                The account-manager list could not be loaded, so this picker is empty. Reload
                before changing it.
              </p>
            )}
          </div>
        ) : (
          <InfoField label="Account manager" value={facility.account_manager_name ?? null} />
        )}
        <div>
          <InlineEditField
            label="Sold by"
            value={getValue('sold_by_eid')}
            onChange={(v) => setDraftField('sold_by_eid', v == null ? null : String(v))}
            type="select"
            options={soldByOptions.map((b) => ({ value: b.employee_id, label: b.name }))}
            placeholder="Not recorded"
            fieldKey="sold_by_eid"
            displayMode="edit"
          />
          {bddError && <p className="mt-1 text-xs text-st-warn-fg">{bddError}</p>}
        </div>
        <InlineEditField
          label="Usables account"
          value={getValue('tcs_provides_usables')}
          onChange={(v) => setDraftField('tcs_provides_usables', v)}
          type="select"
          options={USABLES_OPTIONS}
          placeholder="Not set"
          fieldKey="tcs_provides_usables"
          displayMode="edit"
        />
        <div>
          <InlineEditField
            label="Facility type"
            value={draftedType}
            onChange={(v) => setDraftField('facility_type', v == null ? null : String(v))}
            type="select"
            options={NAS_FACILITY_TYPE_OPTIONS.map((t) => ({
              value: t,
              label: facilityTypeLabel(t),
            }))}
            fieldKey="facility_type"
            displayMode="edit"
          />
          {typeRefusal ? (
            <div
              role="alert"
              data-testid="facility-type-refusal"
              className="mt-2 rounded border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg"
            >
              {typeRefusal}
            </div>
          ) : (
            <p className="mt-1 text-xs text-tcs-text-muted">
              The type decides where this building’s square footage and room counts are kept, so
              only a change that leaves them where they are can be made here.
            </p>
          )}
        </div>
      </dl>
    ) : (
      <>
        <dl className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <InfoField label="Facility name" value={facility.facility_name} full />
          <InfoField label="Facility ID" value={facility.facility_id} />
          <InfoField label="Account ID" value={facility.account_id ?? null} />
          <InfoField label="Facility type" value={facilityTypeLabel(facility.facility_type)} />
          {/* Who won it. Carried from the onboarding card and kept here because
              the card closes and this fact outlives it by years. */}
          <InfoField label="Sold by" value={soldByName} />
          <InfoField label="Market" value={facility.market_region ?? null} />
          <InfoField label="State" value={facility.state ?? null} />
          <InfoField label="Account manager" value={facility.account_manager_name ?? null} />
          <InfoField label="Status" value={facility.status ?? null} />
          <InfoField
            label="Usables account"
            value={usablesLabel(facility.tcs_provides_usables)}
          />
          <InfoField label="Address" value={facility.facility_address ?? null} full />
        </dl>
        {/* CW-C Phase 2 (2026-08-04) — Ben's rule: every facility must have an
            address. Missing is not acceptable state, so it surfaces here as a
            fixable prompt. It now opens the card's own window rather than
            carrying a second save of its own. */}
        <MissingAddressPrompt facility={facility} onOpenEdit={() => setMode('edit')} />
        <div className="mt-3">
          <NavigateButton target={navTarget} variant="link" />
        </div>
      </>
    );

  return (
    <div id="info-card-account-summary" className="scroll-mt-24">
      <CardWithModeToggle
        title="Account Summary"
        mode={mode}
        onModeChange={setMode}
        hasChanges={hasChanges}
        onSave={handleCardSave}
        onCancel={() => setDraft({})}
        description="Everything the building itself is. Written together when you press Save."
        elsewhere={elsewhere}
      >
        {summaryBody}
      </CardWithModeToggle>
    </div>
  );
}

// ============================================================================
// MissingAddressPrompt — CW-C Phase 2 (2026-08-04)
//
// Ben's rule: every facility must have an address. This is the fixable surface
// for the same condition detector D5 reports nightly as
// facility_address_missing (routed to the owning AM, reason blocked_on_me).
//
// Renders NOTHING when an address is present, and nothing for Closed /
// Cancelled facilities — D5 only covers Active/Onboarding, and the card should
// not nag about a site that is gone.
//
// 2026-09-16 (info-tab-one-edit-door) — it no longer carries a save of its own.
// It had a text box and a "Save address" button that wrote straight to the
// facility, which is a second door onto the very field the card's window now
// owns, and it was hidden behind the same struck role list. It is now a prompt
// that opens that window, so there is exactly one way to write an address and
// anyone who can sign in can use it.
// ============================================================================

function MissingAddressPrompt({
  facility,
  onOpenEdit,
}: {
  facility: Facility;
  /** Opens the Account Summary window, where the address is typed. */
  onOpenEdit: () => void;
}) {
  const hasAddress = Boolean(facility.facility_address?.trim());
  const inScope = facility.status === 'Active' || facility.status === 'Onboarding';
  if (hasAddress || !inScope) return null;

  return (
    <div className="mt-3 rounded-lg border border-rag-amber/40 bg-rag-amber/5 p-3">
      <p className="text-xs font-semibold text-tcs-text">No address on file</p>
      <p className="mt-1 text-xs text-tcs-text-secondary">
        Navigation falls back to an unverified map pin without one, and the nightly integrity
        scan will keep flagging this facility to its account manager.
      </p>
      <button
        type="button"
        onClick={onOpenEdit}
        className="mt-2 min-h-[44px] rounded-md bg-tcs-inverse px-3 text-xs font-semibold text-tcs-inverse-ink"
      >
        Add the address
      </button>
    </div>
  );
}

// ============================================================================
// Points of Contact — ONE Edit button, ONE window, ONE Save.
//
// 2026-09-16 (info-tab-one-edit-door). The card used to carry three separate
// doors in its header — "Manage", "Add contact", and a pencil on every row —
// each opening a different modal with its own save. It now has one Edit button
// like every other card on this tab, and the window holds the people on the
// card with their details as plain fields.
//
// ⛔ ADDING, REMOVING AND REORDERING PEOPLE IS STILL THE POC MANAGER'S JOB, and
// it is reached as a ROUTE from inside the window rather than as a second
// button in the header. It is a real screen doing a different thing — curating
// which of an account's people appear on which building, in what order, and who
// is primary — and folding it into a field list would have lost that.
//
// ⛔ THIS IS THE ONE CARD WHOSE SAVE IS NOT A SINGLE STATEMENT, and the reason
// is in the data, not in the code: the window can change several DIFFERENT
// people's rows at once, and one UPDATE cannot carry different values to
// different rows. Each changed person is one commitWrite, and the window names
// which saved and which did not rather than collapsing a partial failure into a
// total one — the defect saveDraftUnits exists to prevent.
// ============================================================================

function PointsOfContactCard({
  contacts,
  facility_id,
  account_id,
  onRefresh,
  onGoToServiceLines,
}: {
  contacts: FacilityContactRow[];
  facility_id: string;
  account_id: string | null;
  onRefresh: () => void;
  onGoToServiceLines: () => void;
}) {
  const [managerOpen, setManagerOpen] = useState(false);
  const [mode, setMode] = useState<CardMode>('view');
  // Keyed contact_id + column, so two people's phone numbers never collide.
  const [draft, setDraft] = useState<Record<string, string | number | null>>({});
  const hasChanges = Object.keys(draft).length > 0;
  const { updateFields } = useFacilityFieldUpdate();

  // 2026-06-09 (Model B): the hook returns is_displayed contact_assignments
  // already ordered by display_order. Cap the card; the POC manager curates
  // the full set (display/order/primary/per-site phone+email).
  const DISPLAY_CAP = 5;
  const visible = contacts.slice(0, DISPLAY_CAP);
  const overflow = contacts.length - visible.length;

  // ⛔ WHO SOMEBODY IS, AND WHO THEY ARE AT THIS BUILDING, ARE TWO ROWS.
  //
  // Found by smoke test, not by reading the code. `contacts` is the person
  // everywhere in the company; `contact_assignments` carries the title, phone,
  // email and role for ONE building, and the card READS the override first and
  // falls back to the person. 176 of 262 displayed assignments carry a title
  // override today, 178 a phone, 173 an email, 241 a role.
  //
  // So writing those four to the person master does two wrong things at once:
  // it edits that person at every other building they work at, and the change
  // never appears here, because the override still wins the read. The first
  // version of this card did exactly that and the screen lied about it.
  //
  // The name is the person. The rest is this building.
  const PERSON_FIELDS = [
    { key: 'preferred_name', label: 'Preferred name', hint: 'The person, everywhere.' },
    { key: 'first_name', label: 'First name', hint: 'The person, everywhere.' },
    { key: 'last_name', label: 'Last name', hint: 'The person, everywhere.' },
  ] as const;

  /** key = what the card reads; column = what this building's own row calls it. */
  const SITE_FIELDS = [
    { key: 'job_title', column: 'job_title', label: 'Job title at this building' },
    { key: 'mobile', column: 'assignment_mobile', label: 'Phone at this building' },
    { key: 'email', column: 'assignment_email', label: 'Email at this building' },
    { key: 'contact_role', column: 'contact_role', label: 'Role at this building' },
  ] as const;

  const CONTACT_FIELDS: ReadonlyArray<{ key: string; label: string; hint?: string }> = [
    ...PERSON_FIELDS,
    ...SITE_FIELDS,
  ];

  // Keyed on the ASSIGNMENT, not the person: the same person can appear on two
  // rows, and a contact-keyed draft would have them overwrite each other.
  const rowId = (c: FacilityContactRow) => c.assignment_id ?? c.contact_id;
  const dk = (c: FacilityContactRow, key: string) => `${rowId(c)}::${key}`;

  function storedValue(c: FacilityContactRow, key: string): string | null {
    return ((c as unknown as Record<string, string | null>)[key] ?? null) as string | null;
  }

  function getValue(c: FacilityContactRow, key: string): string | number | null {
    const k = dk(c, key);
    return k in draft ? draft[k] : storedValue(c, key);
  }

  function setDraftField(c: FacilityContactRow, key: string, next: string | number | null) {
    const k = dk(c, key);
    setDraft((prev) => {
      const normalised = next === '' ? null : next;
      if ((storedValue(c, key) ?? null) === normalised) {
        const { [k]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [k]: normalised };
    });
  }

  function displayName(c: FacilityContactRow): string {
    return (
      (c.preferred_name ? `${c.preferred_name} ${c.last_name ?? ''}` : `${c.first_name ?? ''} ${c.last_name ?? ''}`)
        .trim() || 'Unnamed'
    );
  }

  async function handleCardSave(): Promise<{ ok: boolean; error: string | null }> {
    const saved: string[] = [];
    const failed: string[] = [];

    for (const c of visible) {
      const who = displayName(c);
      // The person's own row, and this building's row, get their own patch —
      // see the header above. Neither is written unless something in it changed.
      const personPatch: Record<string, string | number | boolean | null> = {};
      const sitePatch: Record<string, string | number | boolean | null> = {};

      for (const f of PERSON_FIELDS) {
        const k = dk(c, f.key);
        if (k in draft) {
          const v = draft[k];
          personPatch[f.key] = typeof v === 'string' ? v.trim() || null : v;
        }
      }
      for (const f of SITE_FIELDS) {
        const k = dk(c, f.key);
        if (k in draft) {
          const v = draft[k];
          sitePatch[f.column] = typeof v === 'string' ? v.trim() || null : v;
        }
      }

      const drop = (keys: string[]) =>
        setDraft((prev) => {
          const next = { ...prev };
          keys.forEach((key) => delete next[dk(c, key)]);
          return next;
        });

      if (Object.keys(personPatch).length > 0) {
        const res = await updateFields({
          table: 'contacts',
          row_id: c.contact_id,
          pk_column: 'contact_id',
          fields: personPatch,
          label: `save ${who}’s name`,
        });
        if (res.ok) {
          saved.push(`${who} — name`);
          drop(PERSON_FIELDS.map((f) => f.key));
        } else {
          failed.push(`${who}’s name${res.error ? ` — ${res.error}` : ''}`);
        }
      }

      if (Object.keys(sitePatch).length > 0) {
        if (!c.assignment_id) {
          // No row for this person AT this building, so there is nowhere to put
          // a per-building value. Say so rather than writing it to the person.
          failed.push(
            `${who}’s details at this building — ${who} is on this card through the account rather than this building, so a title, phone, email or role for this building specifically has to be set through Manage contacts.`,
          );
        } else {
          const res = await updateFields({
            table: 'contact_assignments',
            row_id: c.assignment_id,
            pk_column: 'assignment_id',
            fields: sitePatch,
            label: `save ${who}’s details at this building`,
          });
          if (res.ok) {
            saved.push(`${who} — at this building`);
            drop(SITE_FIELDS.map((f) => f.key));
          } else {
            failed.push(`${who} at this building${res.error ? ` — ${res.error}` : ''}`);
          }
        }
      }
    }

    if (failed.length > 0) {
      const savedPart = saved.length > 0 ? `Saved: ${saved.join(', ')}. ` : '';
      return { ok: false, error: `${savedPart}Not saved: ${failed.join('; ')}` };
    }
    setDraft({});
    onRefresh();
    return { ok: true, error: null };
  }

  const elsewhere: ElsewhereRow[] = [
    {
      label: 'Who appears here, in what order, and who is primary',
      value:
        contacts.length === 0
          ? 'Nobody on this building yet'
          : `${contacts.length} ${contacts.length === 1 ? 'person' : 'people'} on this building`,
      go: { label: 'Manage contacts', onGo: () => setManagerOpen(true) },
    },
    {
      label: 'Who receives the invoice',
      value: 'Set on the service line, not here',
      go: { label: 'Go to the service lines', onGo: onGoToServiceLines },
    },
  ];

  const contactsBody = (bodyMode: CardMode) =>
    bodyMode === 'edit' ? (
      visible.length === 0 ? (
        <p className="text-sm text-tcs-text-muted">
          Nobody is on this building yet. Use “Manage contacts” below to add the first person.
        </p>
      ) : (
        <div className="space-y-5">
          {visible.map((c) => (
            <div key={c.assignment_id ?? c.contact_id}>
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-tcs-text-secondary">
                  {displayName(c)}
                </h4>
                <span className="text-[11px] text-tcs-text-muted">
                  {c.scope_type === 'Account'
                    ? 'Account-wide'
                    : c.scope_type === 'Facility'
                      ? 'This facility'
                      : (c.scope_type ?? '')}
                  {c.is_primary ? ' · primary' : ''}
                </span>
              </div>
              <dl className="grid grid-cols-1 gap-3">
                {CONTACT_FIELDS.map((f) => (
                  <InlineEditField
                    key={f.key}
                    label={f.label}
                    value={getValue(c, f.key)}
                    onChange={(v) => setDraftField(c, f.key, v)}
                    hint={f.hint}
                    fieldKey={`${f.key}__${rowId(c)}`}
                    displayMode="edit"
                  />
                ))}
              </dl>
            </div>
          ))}
        </div>
      )
    ) : visible.length === 0 ? (
      <p className="text-sm text-tcs-text-muted">
        No contacts on file for this facility yet. Press Edit to add the first one.
      </p>
    ) : (
      <>
        <ul className="space-y-3">
          {visible.map((c) => {
            const scopeLabel =
              c.scope_type === 'Account'
                ? 'Account-wide'
                : c.scope_type === 'Facility'
                  ? 'This facility'
                  : c.scope_type;
            return (
              <li
                key={c.assignment_id ?? c.contact_id}
                className="border-t border-tcs-border pt-3 first:border-t-0 first:pt-0"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-sm font-medium text-tcs-text">{displayName(c)}</div>
                  <span className="whitespace-nowrap text-xs text-tcs-text-muted">
                    {scopeLabel}
                  </span>
                </div>
                {c.job_title && (
                  <div className="mt-0.5 text-sm text-tcs-text-muted">{c.job_title}</div>
                )}
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {c.mobile && (
                    <a href={`tel:${c.mobile}`} className="text-tcs-accent-ink hover:underline">
                      {c.mobile}
                    </a>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="text-tcs-accent-ink hover:underline">
                      {c.email}
                    </a>
                  )}
                </div>
                {c.contact_role && (
                  <div className="mt-1 text-xs text-tcs-text-muted">Role: {c.contact_role}</div>
                )}
              </li>
            );
          })}
        </ul>
        {overflow > 0 && (
          <p className="mt-3 text-xs text-tcs-text-muted">
            +{overflow} more — press Edit, then “Manage contacts”.
          </p>
        )}
      </>
    );

  return (
    <div id="info-card-contacts" className="scroll-mt-24">
      <CardWithModeToggle
        // ⛔ No field-count badge here. The component's badge counts FIELDS, and
        // this card counts PEOPLE — it read "1 field" for one contact, which is
        // both wrong and meaningless. The count goes in the title, where it did
        // before.
        title={`Points of Contact (${contacts.length})`}
        mode={mode}
        onModeChange={setMode}
        hasChanges={hasChanges}
        onSave={handleCardSave}
        onCancel={() => setDraft({})}
        description="Site contacts. Everything on this card is written together when you press Save."
        elsewhere={elsewhere}
      >
        {contactsBody}
      </CardWithModeToggle>
      <PocManagerModal
        open={managerOpen}
        onClose={() => setManagerOpen(false)}
        facility_id={facility_id}
        account_id={account_id}
        onSaved={onRefresh}
      />
    </div>
  );
}
// ============================================================================
// The per-field card-save loop is GONE — 2026-09-16 (info-tab-one-edit-door).
//
// saveDraftUnits and saveDraftFields lived here from 2026-08-13 (CW-O PR1).
// They existed because every Info-tab card saved its draft one field at a time,
// which made a PARTIAL failure possible: some columns committed, the banner
// still read "Failed to save: …", and the AM reported "it doesn't save" while
// the database already held the new value. Jack hit exactly that on FAC-0279.
// Their job was to REPORT that honestly — name what saved as well as what did
// not, and keep the failed values on screen.
//
// Every card on this tab now writes its whole window in one statement, so the
// failure they described cannot happen and there is nothing left for them to
// report. The lesson survives in the one place it still applies: Points of
// Contact can change several DIFFERENT people at once, one UPDATE cannot carry
// different values to different rows, and that card names both sides itself.
// ============================================================================

// ============================================================================
// Security & access — ONE Edit button, ONE window, ONE Save.
//
// This card was already on the one-door pattern; 2026-09-16 moved its edit mode
// from inside the card into the window, and its eleven fields now go in ONE
// statement rather than one round trip per field.
//
// ⛔ The sensitive fields stay sensitive. Alarm code and lock code render as
// ••••  with a Reveal that auto-hides after five seconds in the read view, and
// as a password input with a show-eye in the window. That behaviour lives in
// InlineEditField and has not been touched.
// ============================================================================

function FacilityAccessCard({
  access,
  accessIdOverride,
  onSaveFields,
  onGoToServiceLines,
}: {
  access: FacilityAccessRow | null;
  accessIdOverride: string | null;
  onSaveFields: (
    patch: Record<string, string | number | null>,
  ) => Promise<{ ok: boolean; error: string | null }>;
  onGoToServiceLines: () => void;
}) {
  // When neither the loaded row nor an override exists, the first save will
  // INSERT (rules let the owning AM create the row). Show the section anyway
  // so the AM has a place to write the first value.
  const exists = access != null || accessIdOverride != null;

  // Field metadata in one place so populated count + draft + render all stay
  // aligned. The key is the DB column; the row order is the render order.
  const ACCESS_FIELDS = [
    { key: 'alarm_code',              label: 'Alarm code',              type: 'text',      sensitive: true,  full: false },
    { key: 'arm_disarm_procedure',    label: 'Arm / disarm',            type: 'multiline', sensitive: false, full: false },
    { key: 'monitoring_company',      label: 'Monitoring company',      type: 'text',      sensitive: false, full: false },
    { key: 'monitoring_phone',        label: 'Monitoring phone',        type: 'text',      sensitive: false, full: false },
    { key: 'lock_type',               label: 'Lock type',               type: 'select',    sensitive: false, full: false,
      options: [
        { value: 'Code',       label: 'Code' },
        { value: 'Key',        label: 'Key' },
        { value: 'Card_Badge', label: 'Card / Badge' },
        { value: 'Smart',      label: 'Smart' },
        { value: 'Combo',      label: 'Combo' },
      ] },
    { key: 'lock_code',               label: 'Lock code',               type: 'text',      sensitive: true,  full: false },
    { key: 'key_location',            label: 'Key location',            type: 'text',      sensitive: false, full: false },
    { key: 'badge_issuer',            label: 'Badge issuer',            type: 'text',      sensitive: false, full: false },
    { key: 'badge_obtain_procedure',  label: 'Badge obtain procedure',  type: 'multiline', sensitive: false, full: true  },
    { key: 'other_access_notes',      label: 'Other access notes',      type: 'multiline', sensitive: false, full: true  },
    { key: 'site_cheat_sheet',        label: 'Site cheat sheet',        type: 'multiline', sensitive: false, full: true,  rows: 5 },
  ] as const;

  const populatedCount = ACCESS_FIELDS.filter(({ key }) => {
    const v = (access as Record<string, unknown> | null)?.[key];
    return v != null && v !== '';
  }).length;
  const blankCount = ACCESS_FIELDS.length - populatedCount;
  const { hideBlanks, ToggleButton } = useHideBlanksControl(blankCount);

  const [mode, setMode] = useState<CardMode>('view');
  const [draft, setDraft] = useState<Record<string, string | number | null>>({});
  const hasChanges = Object.keys(draft).length > 0;

  function getValue(key: string): string | number | null {
    if (key in draft) return draft[key];
    const v = (access as Record<string, unknown> | null)?.[key];
    return (v as string | number | null | undefined) ?? null;
  }

  function setDraftField(key: string, next: string | number | null) {
    setDraft((prev) => {
      const original = (access as Record<string, unknown> | null)?.[key] ?? null;
      const normalisedNext = next === '' ? null : next;
      // If the user has typed the original value back, remove from draft.
      if ((original ?? null) === normalisedNext) {
        const { [key]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: normalisedNext };
    });
  }

  async function handleCardSave(): Promise<{ ok: boolean; error: string | null }> {
    // ONE statement. A partial failure is not reported better than it used to
    // be — it is no longer possible.
    const res = await onSaveFields(draft);
    if (res.ok) setDraft({});
    return res;
  }

  const elsewhere: ElsewhereRow[] = [
    {
      label: 'Who has been issued access',
      value: 'Recorded per service line, against the person doing the work',
      go: { label: 'Go to the service lines', onGo: onGoToServiceLines },
    },
  ];

  const accessBody = (bodyMode: CardMode) => (
    <HideBlanksProvider hideBlanks={bodyMode === 'view' ? hideBlanks : false}>
      <dl
        className={
          bodyMode === 'edit' ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 gap-3 md:grid-cols-2'
        }
      >
        {ACCESS_FIELDS.map((f) => (
          <InlineEditField
            key={f.key}
            label={f.label}
            value={getValue(f.key)}
            onChange={(v) => setDraftField(f.key, v)}
            type={f.type as 'text' | 'multiline' | 'select'}
            options={'options' in f ? [...f.options] : undefined}
            sensitive={f.sensitive}
            full={f.full}
            rows={'rows' in f ? f.rows : undefined}
            fieldKey={f.key}
            displayMode={bodyMode}
          />
        ))}
      </dl>
    </HideBlanksProvider>
  );

  return (
    <div id="info-card-access" className="scroll-mt-24">
      <CardWithModeToggle
        title="Security & access"
        icon={<ShieldCheck className="h-4 w-4 text-tcs-text-secondary" />}
        populatedCount={populatedCount}
        totalCount={ACCESS_FIELDS.length}
        mode={mode}
        onModeChange={setMode}
        hasChanges={hasChanges}
        onSave={handleCardSave}
        onCancel={() => setDraft({})}
        hideBlanksToggle={ToggleButton}
        description={
          !exists
            ? 'No access record on file yet — pressing Save will create one for this facility.'
            : 'How to get in. Codes stay hidden until you reveal them. Written together.'
        }
        elsewhere={elsewhere}
      >
        {accessBody}
      </CardWithModeToggle>
    </div>
  );
}
// ============================================================================
// Section: Facility Details (total_sqft + type-specific counts)
// ============================================================================

// DETAIL_FIELD_SPECS — which columns the card offers, per detail table.
//
// 2026-08-13 (CW-O PR2). This used to be an `isAscOrHospital` branch that
// pushed the same three fields at two different tables:
//
//   num_or_suites   ✅ real on both
//   num_preop_bays  ❌ misspelt — the ASC column is num_pre_op_bays
//   num_pacu_bays   ❌ exists on NO detail table, and never has
//
// The reads returned undefined, so both rendered "—" even where data existed
// (85 ASC rows carry num_pre_op_bays). The writes hit PostgREST 42703. Combined
// with the pre-PR1 card save, which reported a partial failure as a total one,
// that is the whole of Jack's "can't edit square footage": total_sqft committed,
// the phantom field 42703'd, the banner cried failure. num_procedure_rooms
// (97 ASC rows populated) was never offered at all, and Dialysis got neither
// chair_count (68 rows) nor treatment_floor_sqft (20 rows).
//
// Every key below is a real column on its own table — verified against
// src/types/database.types.ts and the live DB on 2026-08-13.
//
// 2026-09-17 — RE-KEYED ON facility_type, AND NOW THE ONLY GUARD.
//
// This map used to be keyed on the detail TABLE a facility_type routed to, and
// it had a lockstep partner: public.facility_detail_table_for() decided the
// table, this decided which of that table's columns to offer, and the database
// backstopped the pair — a hospital column sent at a dialysis row was a
// PostgREST 42703 because the column genuinely was not there.
//
// The three tables are now one, so that backstop is gone: every column exists
// on every row. Nothing but this map decides what a surgery centre is asked for
// and what a dialysis centre is asked for. A key here that is not a column on
// public.facility_details is still invisible until someone hits Save, and a
// column offered to the wrong type now SAVES instead of failing. Which is the
// point — figures a type is not asked for are hidden, not lost, and a building
// corrected to another type shows whatever that type asks for — but it means
// this map is worth reading carefully rather than editing quickly.
//
// Every key below is a real column on public.facility_details, verified against
// src/types/database.types.ts and the live schema on 2026-09-17.
//
// DO NOT add num_pacu_bays. It does not exist on purpose.
// The map, its type and the sterile-split rule live in lib/facility-details.ts
// since item 944, so Ask AI's building card asks exactly these fields.

function FacilityDetailsCard({
  details,
  facility_type,
  onSaveFields,
  onGoToAccountSummary,
}: {
  details: FacilityDetailsRow | null;
  facility_type: string | null;
  /** ⛔ The ONLY save this card has. One statement, one audit row. */
  onSaveFields: (
    patch: Record<string, string | number | null>,
  ) => Promise<{ ok: boolean; error: string | null }>;
  onGoToAccountSummary: () => void;
}) {
  const [mode, setMode] = useState<CardMode>('view');
  const [draft, setDraft] = useState<Record<string, string | number | null>>({});

  // 2026-09-17 — the "this type has nowhere to keep square footage" state is
  // gone, because no type has nowhere any more. It was reachable only for
  // facility_type 'Other', which routed to no detail table; Other now keeps its
  // total square footage on the one table like everything else.
  //
  // A type the map has never heard of still falls through to an empty field
  // list rather than a crash — and it says so, naming the type, instead of
  // rendering a card with nothing in it.
  const typeIsKnown = !!facility_type && !!DETAIL_FIELD_SPECS[facility_type];
  if (!typeIsKnown) {
    return (
      <div id="info-card-details" className="scroll-mt-24 rounded-xl border border-tcs-border bg-tcs-bg p-5">
        <div className="flex items-center gap-2">
          <Ruler className="h-4 w-4 text-tcs-text-secondary" />
          <h3 className="text-sm font-semibold text-tcs-text">Facility details</h3>
        </div>
        <p className="mt-2 text-xs text-tcs-text-muted">
          This building&rsquo;s type is{' '}
          <span className="font-medium text-tcs-text">{facilityTypeLabel(facility_type)}</span>,
          which this screen does not have a field list for, so it cannot say what to ask about the
          building. Set the type to one of the five on Account Summary and these fields appear.
          Nothing already recorded has been lost.
        </p>
        <button
          type="button"
          onClick={onGoToAccountSummary}
          className="mt-2 rounded border border-tcs-border bg-tcs-raised px-2.5 py-1 text-xs font-medium text-tcs-accent-ink hover:border-tcs-accent"
        >
          Change it on Account Summary →
        </button>
      </div>
    );
  }

  if (!details) {
    return (
      <div id="info-card-details" className="scroll-mt-24 rounded-xl border border-tcs-border bg-tcs-bg p-5">
        <div className="flex items-center gap-2">
          <Ruler className="h-4 w-4 text-tcs-text-secondary" />
          <h3 className="text-sm font-semibold text-tcs-text">Facility details</h3>
        </div>
        <p className="mt-2 text-xs text-tcs-text-muted">
          Setting up a blank details row for this facility… refresh in a moment if fields don’t
          appear.
        </p>
      </div>
    );
  }

  const sqft = (details.total_sqft as number | null) ?? null;
  // 2026-07-23 (P1 T2 / AFB-0061), reopened 2026-08-13 (CW-O PR3) — the sqft
  // breakdown strip. Total always; Sterile / Non-sterile only where the split
  // means something. Non-sterile is DERIVED — total − sterile — and written in
  // the same statement as its inputs, which is now the only kind of write this
  // card makes.
  //
  // Gated on facility_type — which, since 2026-09-17, is the only thing
  // anything here is gated on. This strip was already keyed that way while the
  // rest of the card keyed on the detail table, precisely because MOB shared
  // the surgery-centre table and keying on the table asked 22 office buildings
  // for sterile square footage. The whole card now works the way this strip
  // already did.
  const hasSterileSplit = facility_type === 'Surgery_Center';
  const storedSterile = hasSterileSplit ? ((details.sterile_sqft as number | null) ?? null) : null;
  const storedNonSterile = hasSterileSplit
    ? ((details.non_sterile_sqft as number | null) ?? null)
    : null;
  const fmtSqft = (n: number | null) => (n == null ? '—' : n.toLocaleString('en-US'));

  // Every field this card offers comes from DETAIL_FIELD_SPECS, keyed on the
  // building's own facility_type — never on a union of two types' lists.
  type DetailField = DetailFieldSpec & { value: number | string | null };
  const DETAIL_FIELDS: DetailField[] = (DETAIL_FIELD_SPECS[facility_type] ?? []).map((spec) => ({
    ...spec,
    value: (details[spec.key] as number | string | null | undefined) ?? null,
  }));

  // A count stored as 0 is "not yet recorded", not a real zero: the sqft import
  // wrote 0 wherever the source cell was blank. We still render the 0 (never
  // "—") plus a hint, because for an endoscopy centre 0 OR suites can be
  // literally true — the AM is the one who can tell.
  const isUnrecordedZero = (f: DetailField) => f.zeroMeansUnknown === true && f.value === 0;
  const populatedCount = DETAIL_FIELDS.filter(
    (f) => f.value != null && f.value !== '' && !isUnrecordedZero(f),
  ).length;
  const detailBlankCount = DETAIL_FIELDS.length - populatedCount;
  const { hideBlanks: detailsHideBlanks, ToggleButton: DetailsToggle } =
    useHideBlanksControl(detailBlankCount);

  const hasChanges = Object.keys(draft).length > 0;

  function getFieldValue(f: DetailField): string | number | null {
    if (f.key in draft) return draft[f.key];
    return f.value;
  }

  function setDraftField(
    key: string,
    next: string | number | null,
    original: string | number | null,
  ) {
    setDraft((prev) => {
      const normalisedNext = next === '' ? null : next;
      if ((original ?? null) === normalisedNext) {
        const { [key]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: normalisedNext };
    });
  }

  // --- the sterile split -----------------------------------------------------
  const numOrNullDraft = (key: string, stored: number | null): number | null => {
    if (!(key in draft)) return stored;
    const v = draft[key];
    return v == null || v === '' ? null : Number(v);
  };
  const effectiveTotal = numOrNullDraft('total_sqft', sqft);
  const effectiveSterile = numOrNullDraft('sterile_sqft', storedSterile);
  const splitTouched = 'sterile_sqft' in draft || 'total_sqft' in draft;
  const derivedNonSterile =
    effectiveTotal == null || effectiveSterile == null ? null : effectiveTotal - effectiveSterile;
  const nonSterileDisplay = splitTouched ? derivedNonSterile : storedNonSterile;

  async function handleCardSave(): Promise<{ ok: boolean; error: string | null }> {
    // Client-side guards and the ONE statement for the whole card, the derived
    // column included — planDetailsPatch (lib/facility-details.ts), the same
    // rule Ask AI's building card runs. A half-applied row is impossible.
    const plan = planDetailsPatch({
      facilityType: facility_type,
      draft,
      storedTotal: sqft,
      storedSterile,
    });
    if (!plan.ok) return { ok: false, error: plan.error };

    const res = await onSaveFields(plan.patch);
    if (res.ok) setDraft({});
    return res;
  }

  const elsewhere: ElsewhereRow[] = [
    {
      label: 'Facility type',
      value: `${facilityTypeLabel(facility_type)} — decides which fields appear here`,
      go: { label: 'Change it on Account Summary', onGo: onGoToAccountSummary },
    },
  ];

  const detailsBody = (bodyMode: CardMode) => (
    <>
      {/* sqft breakdown strip. Total is the display of the editable field
          below. Sterile is editable in the window. Non-sterile is derived and
          never editable anywhere. */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-tcs-border bg-tcs-bg-muted px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-tcs-text-muted">Total sq ft</div>
          <div className="mt-0.5 text-sm font-semibold text-tcs-text">{fmtSqft(effectiveTotal)}</div>
        </div>
        {hasSterileSplit && (
          <>
            <div className="rounded-lg border border-tcs-border bg-tcs-bg-muted px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-tcs-text-muted">Sterile</div>
              {bodyMode === 'edit' ? (
                <input
                  type="number"
                  value={effectiveSterile == null ? '' : String(effectiveSterile)}
                  onChange={(e) =>
                    setDraftField(
                      'sterile_sqft',
                      e.target.value === '' ? null : Number(e.target.value),
                      storedSterile,
                    )
                  }
                  aria-label="Sterile sq ft"
                  data-field="sterile_sqft"
                  placeholder="(not set)"
                  className="mt-0.5 w-full rounded border border-tcs-border bg-tcs-field px-2 py-1 text-sm font-semibold text-tcs-text focus:border-tcs-accent focus:outline-none"
                />
              ) : (
                <div className="mt-0.5 text-sm font-semibold text-tcs-text">
                  {fmtSqft(effectiveSterile)}
                </div>
              )}
            </div>
            <div className="rounded-lg border border-tcs-border bg-tcs-bg-muted px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-tcs-text-muted">
                Non-sterile
              </div>
              <div
                className="mt-0.5 text-sm font-semibold text-tcs-text"
                data-field="non_sterile_sqft"
              >
                {fmtSqft(nonSterileDisplay)}
              </div>
              <p className="mt-0.5 text-[11px] text-tcs-text-muted">derived: total − sterile</p>
            </div>
          </>
        )}
      </div>
      <HideBlanksProvider hideBlanks={bodyMode === 'view' ? detailsHideBlanks : false}>
        <dl
          className={
            bodyMode === 'edit'
              ? 'grid grid-cols-1 gap-4'
              : 'grid grid-cols-1 gap-3 md:grid-cols-2'
          }
        >
          {DETAIL_FIELDS.map((f) => (
            <InlineEditField
              key={f.key}
              label={f.label}
              value={getFieldValue(f)}
              onChange={(v) => setDraftField(f.key, v, f.value)}
              type={f.type}
              options={f.options}
              fieldKey={f.key}
              // Keyed on what is on screen, not on what is stored: once the AM
              // types a real count the hint must go, and if they type 0 it must
              // appear.
              hint={
                f.zeroMeansUnknown === true && getFieldValue(f) === 0
                  ? '0 = not yet recorded'
                  : undefined
              }
              displayMode={bodyMode}
            />
          ))}
        </dl>
      </HideBlanksProvider>
    </>
  );

  return (
    <div id="info-card-details" className="scroll-mt-24">
      <CardWithModeToggle
        title="Facility details"
        icon={<Ruler className="h-4 w-4 text-tcs-text-secondary" />}
        populatedCount={populatedCount}
        totalCount={DETAIL_FIELDS.length}
        mode={mode}
        onModeChange={setMode}
        hasChanges={hasChanges}
        onSave={handleCardSave}
        onCancel={() => setDraft({})}
        hideBlanksToggle={DetailsToggle}
        description="The fields you see depend on the facility type. Written together when you press Save."
        elsewhere={elsewhere}
      >
        {detailsBody}
      </CardWithModeToggle>
    </div>
  );
}

// ============================================================================
// Section: Crew & Sub
// ============================================================================

function CrewAndSubCard({
  assignments,
  facility_id,
  facilityStatus,
  pausedSince,
  hasProposalFor,
  onSaveAssignment,
  onSaveAssignmentFields,
  onRefresh,
  onLineLifeChanged,
}: {
  assignments: ServiceAssignmentRow[];
  facility_id: string;
  /** ⛔ The building's own status — the ONE answer to "is this building live". */
  facilityStatus: string | null;
  /** When the building's own pause started, or null while it is not paused. */
  pausedSince: string | null;
  hasProposalFor: (target_table: string, target_row_id: string | null, field_name: string) => boolean;
  onSaveAssignment: (
    assignmentId: string,
    field: string,
    value: string | number | boolean | null,
  ) => Promise<{ ok: boolean; error: string | null }>;
  /** Each line's ONE save — the whole window through save_service_line(). */
  onSaveAssignmentFields: (
    assignmentId: string,
    fields: Record<string, string | number | boolean | null>,
  ) => Promise<{ ok: boolean; error: string | null }>;
  onRefresh: () => void;
  /** After "Not started yet" or "Set start date": re-read the lines AND the building. */
  onLineLifeChanged: () => void;
}) {
  // Track which assignment currently has a rate proposal modal open.
  const [stopTarget, setStopTarget] = useState<
    { assignment_id: string; label: string; startedOn: string | null } | null
  >(null);
  const [rcpModal, setRcpModal] = useState<{
    assignmentId: string;
    field: 'billing_rate' | 'sub_rate_monthly' | 'sub_hourly_rate';
    label: string;
    currentValue: number | null;
    /** The figure is per hour (items 831/758). */
    perHour?: boolean;
  } | null>(null);

  // 2026-06-15 — sub-management gate. Mirrors DB can_manage_subs(): the assigned
  // AM can manage subs on their OWN facilities, not just full-authority roles.
  const { canManage } = useCanManageSubs(facility_id);

  // Task 2 (2026-07-14): inline "Add service line" creates another
  // service_assignments row directly (the post-build equivalent of the SOP
  // intake repeater — day porter, monthly carpet, etc.).
  const [addOpen, setAddOpen] = useState(false);

  // End a service line — sets assignment_status='Ended' (chat-track's trigger
  // emits the sub_stopped finance notification). Confirm first; refetch after.
  // ⛔ THIS USED TO BE A BROWSER POP-UP that asked "End this service line?" and,
  // on OK, wrote the word "Ended" and nothing else. It is where 77 stopped lines
  // with no last night came from, which is why nobody could answer when we
  // stopped cleaning a building. Stopping a line now asks for the night, through
  // the one function that stops one (TCSD-0188/0194).
  function endAssignment(assignmentId: string) {
    const a = assignments.find((x) => x.assignment_id === assignmentId);
    setStopTarget({
      assignment_id: assignmentId,
      label: a?.service_label ? `${a.service_label} at this building` : 'this service line',
      startedOn: (a as { start_date?: string | null } | undefined)?.start_date ?? null,
    });
  }

  // ⛔ A LINE THAT NEVER STARTED IS REMOVED, NOT STOPPED. Stopping asks for the
  // last night and refuses a date before the first night. On a building that
  // has not opened, both available answers are false: name a night and you have
  // claimed we cleaned it; say the night is unknown and the line is marked
  // finished with the date missing — the very signal Finance uses to chase REAL
  // unpaid work. A setup mistake became a false chase. Offered INSTEAD of
  // stopping while the building has not opened, never alongside it.
  const [removeTarget, setRemoveTarget] = useState<
    { assignment_id: string; label: string } | null
  >(null);
  function removeNeverStartedLine(assignmentId: string) {
    const a = assignments.find((x) => x.assignment_id === assignmentId);
    setRemoveTarget({
      assignment_id: assignmentId,
      label: a?.service_label
        ? a.service_label
        : 'this service line',
    });
  }

  if (assignments.length === 0) {
    return (
      <div
        id="info-card-crew-and-sub"
        className="scroll-mt-24 rounded-xl border border-tcs-border bg-tcs-bg p-5"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-tcs-text">Crew &amp; Sub</h3>
          {canManage && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center gap-1 rounded border border-tcs-border bg-tcs-raised px-2 py-1 text-xs font-medium text-tcs-text hover:bg-tcs-surface"
              >
                <Plus className="h-3.5 w-3.5" /> Add service line
              </button>
              {/* ⛔ "Assign sub" is gone. It was the only way to record a first
                  sub, it lived on the building rather than on a line, and the
                  path behind it invented lines priced at the whole building.
                  A sub is recorded on the line, so a building with no line has
                  nowhere to put one yet — add the line first. */}
            </div>
          )}
        </div>
        <p className="mt-2 text-sm text-tcs-text-muted">
          {/* This now means what it says: neither a running line NOR one booked
              to start. Until 2026-09-11 it also appeared on buildings whose
              lines were agreed and simply had not started yet. */}
          No service lines on file for this facility — none running, and none
          booked to start.
          {canManage
            ? ' Use “Add service line” to record what is being done here — the sub and what they are paid go on the line itself.'
            : ''}
        </p>
        <AddServiceLineModal
          open={addOpen}
          facilityId={facility_id}
          onClose={() => setAddOpen(false)}
          onAdded={onRefresh}
        />
      </div>
    );
  }

  return (
    <div id="info-card-crew-and-sub" className="scroll-mt-24 space-y-4">
      {canManage && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-tcs-text-secondary">
            Service lines ({assignments.length})
          </span>
          {/* Adding a LINE is not editing a card — it creates one. It stays on
              the list, beside the count, not inside any card's window. */}
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1 rounded border border-tcs-border bg-tcs-raised px-2 py-1 text-xs font-medium text-tcs-text hover:bg-tcs-surface"
          >
            <Plus className="h-3.5 w-3.5" /> Add service line
          </button>
        </div>
      )}
      {assignments.map((a) => (
        <CrewAndSubAssignmentBlock
          key={a.assignment_id}
          a={a}
          // Every OTHER line on this building, so the sub control can offer to
          // apply the same sub to them. 9 of 9 two-line buildings have one sub
          // doing everything.
          siblingLines={assignments.filter((x) => x.assignment_id !== a.assignment_id)}
          facilityStatus={facilityStatus}
          pausedSince={pausedSince}
          canManage={canManage}
          hasProposalFor={hasProposalFor}
          onSaveAssignment={onSaveAssignment}
          onSaveAssignmentFields={onSaveAssignmentFields}
          onEnd={endAssignment}
          onRemoveNeverStarted={removeNeverStartedLine}
          onRefresh={onRefresh}
          onLineLifeChanged={onLineLifeChanged}
          setRcpModal={setRcpModal}
        />
      ))}
      <AddServiceLineModal
        open={addOpen}
        facilityId={facility_id}
        onClose={() => setAddOpen(false)}
        onAdded={onRefresh}
      />
      <StopServiceLineDialog
        open={stopTarget !== null}
        assignmentId={stopTarget?.assignment_id ?? ''}
        label={stopTarget?.label ?? 'this service line'}
        startedOn={stopTarget?.startedOn ?? null}
        onClose={() => setStopTarget(null)}
        onStopped={() => onRefresh()}
      />

      <RemoveNeverStartedLineDialog
        open={removeTarget !== null}
        assignmentId={removeTarget?.assignment_id ?? ''}
        label={removeTarget?.label ?? 'this service line'}
        onClose={() => setRemoveTarget(null)}
        onRemoved={() => onRefresh()}
      />

      <RateChangeModal
        open={rcpModal !== null}
        onClose={() => setRcpModal(null)}
        assignment_id={rcpModal?.assignmentId ?? ''}
        field_name={rcpModal?.field ?? 'billing_rate'}
        field_label={rcpModal?.label ?? ''}
        current_value={rcpModal?.currentValue ?? null}
        per_hour={rcpModal?.perHour ?? false}
        // A rate change moves the line and may move the building's figures:
        // re-read both, so nothing on the page is stale (items 831/758).
        onRecorded={() => {
          onLineLifeChanged();
        }}
      />
    </div>
  );
}

// Per-service-line card. ONE Edit button, ONE window, ONE Save — and the whole
// window goes through save_service_line() in a single call.
//
// 2026-09-16 (info-tab-one-edit-door). Every field here used to save on its own
// the moment you tabbed out of it, the incentive-pay block saved on blur and on
// every tick, and "End line" was a third button in the header.
//
// ⛔ THE MONEY KEEPS THE SWITCH BEN RULED ON 2026-09-15, and the switch is
// rateChangeApplies(), which says exactly what save_service_line() says. Before
// the building opens, the account manager types the first price and it saves —
// naming a price for the first time is not a change to anything. Once it opens,
// the rate is NOT a field in this window at all: it appears under "Changed
// somewhere else" with its current value and a button into the rate-change
// process, which is the only thing that writes it. There is no second door.
//
// ⛔ THE SUB KEEPS ITS OWN CONTROL, for the same kind of reason. Naming a first
// sub and swapping one are two different processes behind one box — the second
// ends an arrangement on a named night, re-points open incentive payouts and
// tells Finance — and SubOnTheLineField is the one door onto both (Ben,
// 2026-09-15). Flattening it into a drafted field would lose that.
export function CrewAndSubAssignmentBlock({
  a,
  siblingLines,
  facilityStatus,
  pausedSince,
  canManage,
  hasProposalFor,
  onSaveAssignment,
  onSaveAssignmentFields,
  onEnd,
  onRemoveNeverStarted,
  onRefresh,
  onLineLifeChanged,
  setRcpModal,
}: {
  a: ServiceAssignmentRow;
  siblingLines: ServiceAssignmentRow[];
  /** ⛔ The BUILDING's own status, passed down. This block never decides it. */
  facilityStatus: string | null;
  /** When the building's own pause started, or null while it is not paused. */
  pausedSince: string | null;
  canManage: boolean;
  hasProposalFor: (target_table: string, target_row_id: string | null, field_name: string) => boolean;
  onSaveAssignment: (
    assignmentId: string,
    field: string,
    value: string | number | boolean | null,
  ) => Promise<{ ok: boolean; error: string | null }>;
  /** The card's ONE save — the whole window through save_service_line(). */
  onSaveAssignmentFields: (
    assignmentId: string,
    fields: Record<string, string | number | boolean | null>,
  ) => Promise<{ ok: boolean; error: string | null }>;
  onEnd: (assignmentId: string) => void | Promise<void>;
  onRemoveNeverStarted: (assignmentId: string) => void;
  onRefresh: () => void;
  onLineLifeChanged: () => void;
  setRcpModal: (m: {
    assignmentId: string;
    field: 'billing_rate' | 'sub_rate_monthly' | 'sub_hourly_rate';
    label: string;
    currentValue: number | null;
    /** The figure is per hour (items 831/758). */
    perHour?: boolean;
  } | null) => void;
}) {
  // ⛔ ONE ANSWER, ASKED ONCE. The building decides whether money is moving; the
  // line's own status decides whether this job is one of the ones moving it.
  const buildingHasOpened = facilityIsLive(facilityStatus);
  const moneyIsARateChange = rateChangeApplies(facilityStatus, a.assignment_status);
  // Item 904 (TCSD-0077: never filtered off a screen, marked instead). A
  // paused line shows its kept figures but writes nothing while paused — a
  // change typed here would skip the rate-change process the moment the
  // building resumes.
  const isPaused = a.assignment_status === 'Suspended';

  const [mode, setMode] = useState<CardMode>('view');
  const [draft, setDraft] = useState<Record<string, string | number | boolean | null>>({});
  const hasChanges = Object.keys(draft).length > 0;

  // ⛔ SIGNED, NOT STARTED (item 847). The existing Scheduled word with no first
  // night. "Not started yet" is offered on a running line; "Set start date" on a
  // line that is not started, and on a booked line once the building is open
  // (a booked line at a building that has not opened starts with the building,
  // whose dates move on its own form — TCSD-0294).
  const [holdOpen, setHoldOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const lineName = a.service_label ?? 'this service line';
  const notStarted = a.assignment_status === 'Scheduled' && !a.start_date;
  const booked = a.assignment_status === 'Scheduled' && !!a.start_date;
  // ⛔ Never the last running service of a started building (Ben, 2026-10-23):
  // it started with the building. The database refuses it too
  // (TCS_NOT_STARTED_ONLY_RUNNING_LINE); this only stops offering it.
  const canHold = canManage && a.assignment_status === 'Active'
    && (!buildingHasOpened || siblingLines.some((x) => x.assignment_status === 'Active'));
  const canSetStart = canManage && (notStarted || (booked && buildingHasOpened));
  const hold = notStarted ? notStartedReason(a.notes) : null;

  const LINE_FIELDS: Array<{
    key: string;
    label: string;
    type?: 'text' | 'number' | 'date' | 'multiline' | 'select';
    full?: boolean;
  }> = [
    { key: 'worker_name', label: 'Worker name' },
    { key: 'worker_phone', label: 'Worker phone' },
    { key: 'training_status', label: 'Training status' },
    { key: 'training_completion_date', label: 'Training completion date', type: 'date' },
    { key: 'facility_access_issued', label: 'Facility access issued' },
    { key: 'crew_size', label: 'Crew size', type: 'number' },
    { key: 'or_count', label: 'OR count', type: 'number' },
    { key: 'pr_count', label: 'PR count', type: 'number' },
    { key: 'sub_agreement_url', label: 'Sub agreement URL', full: true },
    { key: 'notes', label: 'Notes', type: 'multiline', full: true },
  ];

  // Items 831/758: a line billed by the hour bills the client per hour; a line
  // that pays its sub by the hour pays per hour. Read from the LINE.
  const billsHourly = isHourlyBilledLine(a);
  const paysHourly = paysSubByTheHour(a);

  // The rate is a field ONLY where save_service_line() would accept it as one.
  // ⛔ On a line that pays by the hour there is no monthly sub box: the hourly
  // rate is what the sub is paid, and the database refuses a typed monthly figure.
  const MONEY_FIELDS: Array<{ key: string; label: string }> = moneyIsARateChange
    ? []
    : [
        { key: 'billing_rate', label: billsHourly ? 'Billing rate ($/hr)' : 'Billing rate ($/mo)' },
        paysHourly
          ? { key: 'sub_hourly_rate', label: 'Contract rate ($/hr)' }
          : { key: 'sub_rate_monthly', label: 'Contract rate ($/mo)' },
      ];

  const blankCount = LINE_FIELDS.filter((f) => {
    const v = (a as unknown as Record<string, unknown>)[f.key];
    return v == null || v === '';
  }).length;
  const { hideBlanks, ToggleButton } = useHideBlanksControl(blankCount);

  function stored(key: string): string | number | boolean | null {
    return ((a as unknown as Record<string, unknown>)[key] ?? null) as
      | string
      | number
      | boolean
      | null;
  }

  function getValue(key: string): string | number | boolean | null {
    return key in draft ? draft[key] : stored(key);
  }

  function setDraftField(key: string, next: string | number | boolean | null) {
    setDraft((prev) => {
      const normalised = next === '' ? null : next;
      if ((stored(key) ?? null) === normalised) {
        const { [key]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: normalised };
    });
  }

  async function handleCardSave(): Promise<{ ok: boolean; error: string | null }> {
    // ONE call. save_service_line() takes the whole patch, applies what it
    // accepts and names what it refuses per field, so the window can say
    // exactly which values it kept rather than reporting a total failure.
    const res = await onSaveAssignmentFields(a.assignment_id, draft);
    if (res.ok) setDraft({});
    return res;
  }

  const money = (n: number | null | undefined) =>
    n == null
      ? '(not set)'
      : `$${Number(n).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} / month`;
  // Per hour on a line billed or paid by the hour (items 831/758).
  const hourly = (n: number | null | undefined) => (n == null ? '(not set)' : perHour(Number(n)));
  const subMonthlyEst = paysHourly
    ? scheduleMonthlyEstimate(a.sub_hourly_rate, a.sub_hours_per_day, a.service_days_per_week)
    : null;
  // Under "What we pay the sub" on an hourly line: what its set hours come to,
  // worked out here and never typed — or why there is no monthly figure.
  const subHourlyNote = paysHourly
    ? subMonthlyEst != null
      ? `about $${subMonthlyEst.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} a month at ${a.sub_hours_per_day} hours × ${a.service_days_per_week} days — worked out, not typed.`
      : NO_SET_HOURS_SENTENCE
    : null;

  const elsewhere: ElsewhereRow[] = [
    // ⛔ When the building is open these are the ONLY place the money appears.
    ...(moneyIsARateChange
      ? ([
          {
            label: 'What we bill the client',
            value: billsHourly ? hourly(a.billing_rate) : money(a.billing_rate),
            note: hasProposalFor('service_assignments', a.assignment_id, 'billing_rate')
              ? 'A rate change is already waiting on this line.'
              : billsHourly
                ? `${HOURLY_ACTUALS_LABEL}. This building is open, so a change to the hourly rate goes through the rate-change process and Finance is told.`
                : 'This building is open, so a change to what we bill goes through the rate-change process and Finance is told.',
            go: {
              label: 'Start a rate change',
              onGo: () =>
                setRcpModal({
                  assignmentId: a.assignment_id,
                  field: 'billing_rate',
                  label: billsHourly ? 'Billing rate (per hour)' : 'Billing rate',
                  currentValue: a.billing_rate ?? null,
                  perHour: billsHourly,
                }),
            },
          },
          // ⛔ On a line that pays by the hour, "What we pay the sub" shows and
          // changes the HOURLY rate (Ben 2026-10-12, TCSD-0492). The monthly
          // figure is only what the set hours come to, and is never typed.
          paysHourly
            ? {
                label: 'Subcontractor contract rate',
                value: hourly(a.sub_hourly_rate),
                note: hasProposalFor('service_assignments', a.assignment_id, 'sub_hourly_rate')
                  ? `${subHourlyNote} A rate change is already waiting on this line.`
                  : `${subHourlyNote} Changed through the same rate-change process, so the margin and the payout move together.`,
                go: {
                  label: 'Start a rate change',
                  onGo: () =>
                    setRcpModal({
                      assignmentId: a.assignment_id,
                      field: 'sub_hourly_rate',
                      label: 'Contract rate (hourly)',
                      currentValue: a.sub_hourly_rate ?? null,
                      perHour: true,
                    }),
                },
              }
            : {
                label: 'Subcontractor contract rate',
                value: money(a.sub_rate_monthly),
                note: hasProposalFor('service_assignments', a.assignment_id, 'sub_rate_monthly')
                  ? 'A rate change is already waiting on this line.'
                  : 'Changed through the same rate-change process, so the margin and the payout move together.',
                go: {
                  label: 'Start a rate change',
                  onGo: () =>
                    setRcpModal({
                      assignmentId: a.assignment_id,
                      field: 'sub_rate_monthly',
                      label: 'Contract rate (monthly)',
                      currentValue: a.sub_rate_monthly ?? null,
                    }),
                },
              },
        ] as ElsewhereRow[])
      : []),
    {
      label: 'What kind of work this line is',
      value: a.service_label ?? 'Not yet named',
      note: 'Named from the one list of services. Billing method, invoice-to and the line’s status are managed by Finance and HR — submit a ticket if one needs to change.',
    },
    { label: 'Billing method', value: a.billing_method ?? '(not set)', note: 'Managed by Finance.' },
    { label: 'Invoice to', value: a.invoice_to ?? '(not set)', note: 'Managed by Finance.' },
    ...(canManage && a.assignment_status !== 'Ended'
      ? ([
          {
            label: buildingHasOpened ? 'Stop this service line' : 'Remove this service line',
            value: a.start_date
              ? `Started ${new Date(`${a.start_date}T00:00:00`).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}`
              : 'No start date recorded',
            // ⛔ A LINE THAT NEVER STARTED IS REMOVED, NOT STOPPED. Stopping
            // asks for the last night; on a building that has not opened both
            // available answers are false, and a line marked finished with no
            // date is the very signal Finance uses to chase real unpaid work.
            note: buildingHasOpened
              ? 'Stopping asks for the last night we cleaned, so the record says when the work ended.'
              : 'This building has not opened, so there is no last night to name — the line is removed rather than stopped.',
            go: {
              label: buildingHasOpened ? 'Stop the service line' : 'Remove the line',
              onGo: () =>
                buildingHasOpened
                  ? void onEnd(a.assignment_id)
                  : onRemoveNeverStarted(a.assignment_id),
            },
          },
        ] as ElsewhereRow[])
      : []),
  ];

  const lineBody = (bodyMode: CardMode) => (
    <>
    {/* The reason, under the line. With no start date it is the only record of
        what we are waiting on (item 847). */}
    {bodyMode === 'view' && notStarted && (
      <p className="mb-3 rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-secondary">
        <span className="font-medium text-tcs-text">No start date. </span>
        {hold ? (
          <>
            Why: <em>{hold.reason}</em>
            {hold.who || hold.on ? (
              <span className="text-tcs-text-muted">
                {' '}
                — {[hold.who, hold.on].filter(Boolean).join(', ')}
              </span>
            ) : null}
          </>
        ) : (
          'No reason is recorded on the line.'
        )}
      </p>
    )}
    <HideBlanksProvider hideBlanks={bodyMode === 'view' ? hideBlanks : false}>
      <dl
        className={
          bodyMode === 'edit' ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 gap-3 md:grid-cols-2'
        }
      >
        {/* ⛔ The sub is recorded ON THE LINE and nowhere else, through the one
            control that knows the difference between naming a first sub and
            swapping one. Withheld while paused (item 904) — this field has
            its own door onto the save, outside the card's Edit window. */}
        <SubOnTheLineField
          a={a}
          canManage={canManage && !isPaused}
          otherLines={siblingLines}
          onSaveAssignment={onSaveAssignment}
          onRefresh={onRefresh}
        />
        {LINE_FIELDS.map((f) => (
          <InlineEditField
            key={f.key}
            label={f.label}
            value={getValue(f.key) as string | number | null}
            onChange={(v) => setDraftField(f.key, v)}
            type={f.type}
            full={f.full}
            fieldKey={`${f.key}__${a.assignment_id}`}
            displayMode={bodyMode}
          />
        ))}
        {MONEY_FIELDS.map((f) => {
          const raw = getValue(f.key) as number | null;
          // While paused this field is view-only (the window never opens on
          // it), so the figure reads as a dollar amount here — the same bare
          // "$3,150.00" the resume hint already uses (moneyWithCents).
          const displayValue =
            isPaused && bodyMode === 'view' && raw != null ? moneyWithCents(Number(raw)) : raw;
          return (
            <InlineEditField
              key={f.key}
              label={f.label}
              value={displayValue}
              onChange={(v) => setDraftField(f.key, v)}
              type="number"
              fieldKey={`${f.key}__${a.assignment_id}`}
              displayMode={bodyMode}
              hint={
                bodyMode === 'edit'
                  ? 'This building has not opened yet, so the first price is typed here. Once it opens, changing it goes through the rate-change process.'
                  : undefined
              }
            />
          );
        })}
      </dl>
      <IncentivePayBlock
        a={a}
        mode={bodyMode}
        getValue={getValue}
        setDraftField={setDraftField}
      />
    </HideBlanksProvider>
    </>
  );

  return (
    <>
    <CardWithModeToggle
      title={`Crew & Sub — ${a.service_label ?? 'Not yet named'}`}
      populatedCount={LINE_FIELDS.length - blankCount}
      totalCount={LINE_FIELDS.length}
      mode={mode}
      onModeChange={setMode}
      hasChanges={hasChanges}
      onSave={handleCardSave}
      onCancel={() => setDraft({})}
      hideBlanksToggle={ToggleButton}
      readOnly={isPaused}
      extraActions={
        isPaused ? (
          <span className="inline-flex items-center rounded-full border border-tcs-border bg-tcs-bg-muted px-2 py-0.5 text-[11px] font-medium text-tcs-text-secondary">
            {pausedSince ? `Paused since ${fmtDate(pausedSince)}` : 'Paused'}
          </span>
        ) : a.assignment_status === 'Scheduled' ? (
          <>
            <span className="inline-flex items-center rounded-full border border-tcs-border bg-tcs-bg-muted px-2 py-0.5 text-[11px] font-medium text-tcs-text-secondary">
              {a.start_date
                ? `Starts ${new Date(`${a.start_date}T00:00:00`).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })} — not counted in cost yet`
                : 'Not started'}
            </span>
            {canSetStart && (
              <button
                type="button"
                onClick={() => setStartOpen(true)}
                className="rounded border border-tcs-accent bg-tcs-accent-ink px-2 py-1 text-xs font-medium text-tcs-accent-on hover:opacity-90"
              >
                Set start date
              </button>
            )}
          </>
        ) : canHold ? (
          <button
            type="button"
            onClick={() => setHoldOpen(true)}
            className="rounded border border-tcs-border bg-tcs-raised px-2 py-1 text-xs text-tcs-text hover:bg-tcs-surface"
          >
            Not started yet
          </button>
        ) : a.assignment_status === 'Ended' ? (
          <span className="rounded bg-tcs-surface px-2 py-1 text-xs font-medium text-tcs-text-secondary">
            Ended
          </span>
        ) : null
      }
      description={
        isPaused
          ? 'This line is paused with the building. Its figures are read-only here and return exactly as they are when the building resumes.'
          : moneyIsARateChange
            ? 'One window for this service line. The money is not in here — this building is open, so it goes through the rate-change process.'
            : 'One window for this service line. This building has not opened, so the first prices are typed here.'
      }
      elsewhere={elsewhere}
    >
      {lineBody}
    </CardWithModeToggle>
    <MarkNotStartedDialog
      open={holdOpen}
      assignmentId={a.assignment_id}
      label={lineName}
      onClose={() => setHoldOpen(false)}
      onDone={onLineLifeChanged}
    />
    <SetLineStartDialog
      open={startOpen}
      assignmentId={a.assignment_id}
      label={lineName}
      currentStart={a.start_date}
      onClose={() => setStartOpen(false)}
      onDone={onLineLifeChanged}
    />
    </>
  );
}

// ============================================================================
// IncentivePayBlock — sub incentive-pay terms an AM manages on an assignment
// (F7, Jose 2026-06-02). The columns already exist on service_assignments;
// Finance reads the same fields off the assignment row.
//
// 2026-09-16 (info-tab-one-edit-door) — it no longer saves anything itself. It
// used to write on every tick of a checkbox and on blur of each box, with its
// own optimistic state and its own rollback, which is three more saves on a
// card that is meant to have one. It now reads and writes the line's draft and
// goes out with the window's single Save, so a bonus and the sub it belongs to
// can never be half-written.
// ============================================================================
function IncentivePayBlock({
  a,
  mode,
  getValue,
  setDraftField,
}: {
  a: ServiceAssignmentRow;
  mode: CardMode;
  getValue: (key: string) => string | number | boolean | null;
  setDraftField: (key: string, next: string | number | boolean | null) => void;
}) {
  const noMissEnabled = Boolean(getValue('no_misses_bonus_enabled'));
  const complimentEnabled = Boolean(getValue('compliment_incentive_enabled'));
  const amount = getValue('no_misses_bonus_amount');
  const basis = getValue('no_misses_bonus_basis');

  const chk = 'mt-0.5 h-4 w-4 rounded border-tcs-border-strong text-tcs-text';

  // Read view: say what is on, and nothing else. A row of unticked boxes on a
  // read-only card reads as something you can press.
  if (mode === 'view') {
    if (!noMissEnabled && !complimentEnabled) return null;
    return (
      <div className="mt-4 rounded-lg border border-tcs-border bg-tcs-raised p-4">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
          Performance payments
        </h4>
        <ul className="mt-2 space-y-1 text-sm text-tcs-text">
          {noMissEnabled && (
            <li>
              Scope Performance Payment
              {amount != null && amount !== ''
                ? ` — $${Number(amount).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : ' — no amount set'}
              {basis ? ` ${String(basis)}` : ''}
            </li>
          )}
          {complimentEnabled && <li>Compliment payment</li>}
        </ul>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-tcs-border bg-tcs-raised p-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
        Performance payments
      </h4>
      <p className="mt-0.5 text-[11px] text-tcs-text-muted">
        Optional performance payments on this sub&rsquo;s terms. Visible to Finance for payment. Saved with the
        rest of this window.
      </p>

      <div className="mt-3 space-y-3">
        <div className="flex items-start gap-2">
          <input
            id={`no-miss-${a.assignment_id}`}
            type="checkbox"
            checked={noMissEnabled}
            onChange={() => setDraftField('no_misses_bonus_enabled', !noMissEnabled)}
            className={chk}
          />
          <label
            htmlFor={`no-miss-${a.assignment_id}`}
            className="flex-1 select-none text-sm text-tcs-text"
          >
            Scope Performance Payment
            <span className="ml-1 text-[11px] text-tcs-text-muted">
              — pays out when the sub has no missed cleans
            </span>
          </label>
        </div>

        {noMissEnabled && (
          <div className="ml-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-xs text-tcs-text-secondary">
              Scope Performance Payment amount (USD)
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount == null ? '' : String(amount)}
                onChange={(e) =>
                  setDraftField(
                    'no_misses_bonus_amount',
                    e.target.value === '' ? null : Number(e.target.value),
                  )
                }
                className="mt-1 block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
              />
            </label>
            <label className="block text-xs text-tcs-text-secondary">
              Basis
              <input
                type="text"
                value={basis == null ? '' : String(basis)}
                onChange={(e) => setDraftField('no_misses_bonus_basis', e.target.value)}
                placeholder="e.g. per month, per quarter"
                className="mt-1 block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
              />
            </label>
          </div>
        )}

        <div className="flex items-start gap-2">
          <input
            id={`compliment-${a.assignment_id}`}
            type="checkbox"
            checked={complimentEnabled}
            onChange={() => setDraftField('compliment_incentive_enabled', !complimentEnabled)}
            className={chk}
          />
          <label
            htmlFor={`compliment-${a.assignment_id}`}
            className="flex-1 select-none text-sm text-tcs-text"
          >
            Compliment payment
            <span className="ml-1 text-[11px] text-tcs-text-muted">
              — rewards documented facility compliments
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Section: Financial Summary (facility-level rates — read only; the rate is
// the sum of the service lines and is changed on the line, TCSD-0115)
// ============================================================================

function FinancialSummaryCard({
  facility,
  assignments,
  margin,
  cluster,
  onRefresh,
  onGoToServiceLines,
}: {
  facility: Facility;
  assignments: ServiceAssignmentRow[];
  margin: FacilityMarginRow | null;
  cluster: BillingClusterRow | null;
  onRefresh: () => void;
  onGoToServiceLines: () => void;
}) {
  const [mode, setMode] = useState<CardMode>('view');
  const { updateFields } = useFacilityFieldUpdate();
  // Item #706: annual increases booked for a later night (summed below, once the
  // card's figure is known). Called here, above the early return.
  const bookedIncreases = useScheduledAnnualIncreaseRows(facility.facility_id);
  // The rate history page admits the seats seesRateHistory names (its own guard).
  const { profile: viewer } = useAuth();
  const canReadRateHistory = seesRateHistory(viewer);
  // Item 758: an hourly building's billed months, read with the viewer's own
  // rights. Called here, above the early return, like the hook before it.
  const hourlyBuilding =
    (margin != null ? normalizeRateBasis(margin.rate_basis) : deriveRateBasisFallback(facility.monthly_rate)) ===
    'hourly';
  const billed = useBilledMonths(facility.facility_id, hourlyBuilding, hasFinanceCapability(viewer));

  // ⛔ management_fee_pct is a FRACTION in the database — 0.0300 is 3%. The two
  // converters that already exist are the only place that arithmetic lives; a
  // third copy here is how a building ends up billed a 300% management fee.
  const storedFeePct = pctFromKicker(facility.management_fee_pct);
  const [feeDraft, setFeeDraft] = useState<string | null>(null);
  const feeChanged = feeDraft != null && feeDraft !== storedFeePct;

  const fmtMoney = (n: number) =>
    `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  async function handleCardSave(): Promise<{ ok: boolean; error: string | null }> {
    if (!feeChanged) return { ok: true, error: null };
    // An empty box means no fee, which is a real answer here (169 of 220
    // buildings are at 3%, the rest are waived) — not a missing one.
    const fraction = feeDraft!.trim() === '' ? 0 : kickerFromPct(feeDraft!);
    if (fraction == null) {
      return { ok: false, error: 'The management fee has to be a number, like 3 or 2.5.' };
    }
    if (fraction > 1) {
      return {
        ok: false,
        error: `${feeDraft}% is more than the whole invoice. Type the fee as a percentage — 3 means 3%.`,
      };
    }
    const res = await updateFields({
      table: 'facilities',
      row_id: facility.facility_id,
      pk_column: 'facility_id',
      fields: { management_fee_pct: fraction },
      label: 'save the management fee',
    });
    if (!res.ok) return { ok: false, error: res.error };
    setFeeDraft(null);
    onRefresh();
    return { ok: true, error: null };
  }

  // 2026-05-26 billing-cluster-rendering cowork — Branch C (early return).
  // When THIS facility is a bundled service location under another facility's
  // contract (is_billing_primary=false + cluster_primary_facility_id set),
  // suppress the standalone sub-cost / margin display entirely. The local
  // assignment's sub_rate_monthly is 0 by design — the real sub cost is
  // pooled at the primary — so showing the legacy card here misleads the AM
  // (100% margin theater). Render an informational banner that points to the
  // primary facility and surfaces the combined cluster margin instead.
  if (
    margin &&
    margin.is_billing_primary === false &&
    margin.cluster_primary_facility_id
  ) {
    return <BundledServiceLocationCard margin={margin} />;
  }

  // 2026-05-19 facility-detail-consolidation cowork:
  // sub cost is now derived live from service_assignments.sub_rate_monthly
  // (filtered to active assignments). The previous facilities.sub_rate_monthly
  // / margin_monthly / margin_pct cached columns are dropped — drift between
  // Crew & Sub card and Financial Summary card was the bug (FAC-0105 etc).
  // ⛔ BEFORE A BUILDING OPENS, BOTH SIDES COUNT THE SAME LINES.
  //
  // Houston Physicians Hospital read $23,512.84 coming in, $0 going out and
  // 100% margin, because this card counts running lines only and its two lines
  // are booked to start on 1 October. That is correct for the finance book and
  // it reads as broken to the person looking at it — and it hides the only
  // question anyone asks before go-live, which is whether the building is
  // priced the way we agreed.
  //
  // ⛔ THE MONEY VIEWS DO NOT CHANGE. Showing a line and charging for it are
  // different questions. v_finance_all, v_finance_book, the buildings overview
  // and the monthly tie-out all still count running lines only, and they are
  // proved unchanged. This is what the CARD displays, nothing else.
  const buildingIsLive = facilityIsLive(facility.status);
  const countedAssignments = assignments.filter((a) =>
    buildingIsLive
      ? a.assignment_status === 'Active'
      : isRunningOrBooked(a.assignment_status),
  );
  const activeAssignments = countedAssignments;
  const subTotal = activeAssignments.reduce(
    (sum, a) => sum + (typeof a.sub_rate_monthly === 'number' ? a.sub_rate_monthly : 0),
    0,
  );
  // ⛔ Items 831/758: a line that pays its sub by the hour with no set hours
  // has NO monthly figure. It is never added in as $0, and the row says so.
  const hourlyNoFigure = activeAssignments.filter(
    (a) => paysSubByTheHour(a) && a.sub_rate_monthly == null,
  );
  const subCostText =
    activeAssignments.length > 0 && hourlyNoFigure.length === activeAssignments.length
      ? 'No monthly figure'
      : fmtMoney(subTotal);
  const subCostNote =
    hourlyNoFigure.length === 0
      ? null
      : hourlyNoFigure.length === activeAssignments.length
        ? `The sub is paid ${hourlyNoFigure
            .map((a) => (a.sub_hourly_rate != null ? perHour(a.sub_hourly_rate) : 'by the hour'))
            .join(' and ')} and ${hourlyNoFigure.length === 1 ? 'the line has' : 'the lines have'} no set hours.`
        : `Leaves out ${hourlyNoFigure.length} line${hourlyNoFigure.length === 1 ? '' : 's'} paid by the hour with no set hours.`;
  // The one hourly rate the running hourly lines carry, and the one running
  // hourly line whose schedule can be worked out (item 758).
  const hourlyRate = runningHourlyRate(assignments);
  const hourlyLines = assignments.filter(
    (a) => a.assignment_status === 'Active' && isHourlyBilledLine(a),
  );
  const scheduleLine = hourlyLines.length === 1 ? hourlyLines[0] : null;
  // CW-Q Phase 6 — rate basis. Only v_facility_margins knows about
  // billing_type and client_billing_lines, so the basis comes from the margin
  // row; when that read failed (margin === null, the card is deliberately
  // defensive about it) we fall back to what the raw rate can prove on its own,
  // which is never 'hourly'.
  //
  // The rate is taken from the same row as the basis for the same reason — a
  // basis read from the view and an amount read from a separately-fetched
  // facilities row is how a "monthly_flat with no rate" contradiction gets on
  // screen.
  const basis: RateBasis =
    margin != null
      ? normalizeRateBasis(margin.rate_basis)
      : deriveRateBasisFallback(facility.monthly_rate);
  // Before go-live the client side is summed from the same lines as the sub
  // side, so the two halves of the card can never disagree about which lines
  // they are describing. Once the building is open, unchanged.
  // ⛔ A line billed by the hour adds nothing to a monthly figure (items
  // 831/758) — the same rule the building's rate follows once it opens.
  const monthlyCounted = countedAssignments.filter((a) => !isHourlyBilledLine(a));
  const bookedClientRate =
    countedAssignments.length > 0 && monthlyCounted.length === 0
      ? null
      : monthlyCounted.reduce(
          (sum, a) => sum + (typeof a.billing_rate === 'number' ? a.billing_rate : 0),
          0,
        );
  const clientRate = !buildingIsLive
    ? bookedClientRate
    : margin != null
      ? (margin.facility_monthly_rate ?? null)
      : (facility.monthly_rate ?? null);
  // Item #706: an annual increase booked for a later night shows beside today's
  // figure, which does not change until that night.
  const scheduledIncrease = scheduledIncreaseFrom(bookedIncreases, clientRate);
  // Unchanged arithmetic on purpose: this phase changes what is SHOWN, not what
  // is computed. Whether these are shown at all is MarginRows' call via
  // marginAvailability(). On live data (2026-08-14) no facility has
  // monthly_rate = 0, so no figure that rendered before stops rendering now.
  const marginMonthly =
    clientRate != null ? clientRate - subTotal : null;
  const marginPct =
    clientRate != null && clientRate > 0
      ? (marginMonthly! / clientRate)
      : null;
  // Item 904 — a paused building. `countedAssignments` already (correctly,
  // forever) leaves the paused line out of subTotal/marginMonthly above: that
  // arithmetic is the finance tie-out and does not change. What follows is
  // ONLY for the card's own "while paused" display — the figure the paused
  // line itself still carries, for "when service resumes" underneath the $0.
  // `clientRate` is untouched by the pause (TCSD-0076: the rate is kept on
  // purpose, as what service resumes at), so it doubles as the resume figure.
  const isPaused = facility.service_state === 'Suspended';
  const pausedAssignments = assignments.filter((a) => a.assignment_status === 'Suspended');
  const resumeSubTotal = pausedAssignments.reduce(
    (sum, a) => sum + (typeof a.sub_rate_monthly === 'number' ? a.sub_rate_monthly : 0),
    0,
  );
  const resumeMarginMonthly = clientRate != null ? clientRate - resumeSubTotal : null;
  const resumeMarginPct =
    clientRate != null && clientRate > 0 ? (resumeMarginMonthly! / clientRate) : null;
  const flagged = assignments.some((a) => a.assignment_status === 'FLAG');
  const [showBreakdown, setShowBreakdown] = useState(false);
  // 2026-08-26, TCSD-0115 step 3: there is no rate-change proposal on this card any more.
  // The facility rate is the sum of the service lines, the database refuses a hand write to
  // it, and rcp_apply_on_approve refuses a `facilities.monthly_rate` proposal outright — so
  // the modal that raised one had nothing left to submit. Retired with its last caller
  // (TCSD-0119). Per-LINE proposals live on the Service lines card and are untouched.

  // 2026-05-26 billing-cluster-rendering cowork — Branch B trigger.
  // When this facility IS the primary of a multi-member billing cluster,
  // render the existing standalone block AS-IS (its numbers are still
  // correct for the primary) and then append a "Combined cluster" subsection
  // below the dl grid. Solo primaries (cluster_member_count is null or 1)
  // skip this and render exactly like before the cowork.
  const showClusterSubsection =
    margin?.is_billing_primary === true &&
    (margin?.cluster_member_count ?? 0) > 1;

  const elsewhere: ElsewhereRow[] = [
    basis === 'hourly'
      ? {
          // Items 831/758: an hourly building has no monthly rate, by design.
          label: 'Client rate',
          value: hourlyBilledText(hourlyRate),
          note: 'There is no monthly rate: the client pays for the hours worked. The hourly rate is changed on the line.',
          go: { label: 'Go to the service lines', onGo: onGoToServiceLines },
        }
      : {
          label: 'Monthly billing rate',
          value: clientRate == null ? '(no rate)' : fmtMoney(clientRate),
          note: `The sum of this building’s ${activeAssignments.length} ${
            buildingIsLive ? 'active' : 'agreed'
          } service line${activeAssignments.length === 1 ? '' : 's'}. Nobody types it — it follows when a line’s billing rate changes.`,
          go: { label: 'Go to the service lines', onGo: onGoToServiceLines },
        },
    {
      label: 'Subcontractor cost',
      value: subCostText,
      note: 'The sum of what we pay on those same lines. Changed on the line, through the rate-change process once the building is open.',
      go: { label: 'Go to the service lines', onGo: onGoToServiceLines },
    },
    {
      label: 'Gross margin',
      value:
        marginMonthly == null
          ? '(not calculable)'
          : `${fmtMoney(marginMonthly)}${marginPct == null ? '' : ` · ${(marginPct * 100).toFixed(1)}%`}`,
      note: 'Calculated from the two figures above. There is nothing to type.',
      never: true,
    },
    {
      label: 'Supply cost',
      ...supplyCostElsewhereRow(facility.supply_cost_pct ?? null),
      never: true,
    },
  ];

  // ⛔ ONE editable field, and it is here on purpose. The prompt for this run
  // said the Financial summary has no editable fields at all, and the mock
  // routed the management fee to "the account". THERE IS NO ACCOUNT SCREEN in
  // this app today, so obeying that literally would have deleted the only place
  // in the system where a management fee can be waived, with nowhere to send
  // the person instead. management_fee_pct is a plain facilities column — not
  // one of the protected four — so it is drafted and written by this card's own
  // Save like any other field, and the standalone billing card that used to sit
  // at the bottom of this card with a save of its own is gone.
  const moneyBody = (bodyMode: CardMode) =>
    bodyMode === 'edit' ? (
      <dl className="grid grid-cols-1 gap-4">
        <InlineEditField
          label="Management fee (%)"
          value={feeDraft ?? storedFeePct}
          onChange={(v) => setFeeDraft(v == null ? '' : String(v))}
          type="number"
          fieldKey="management_fee_pct"
          displayMode="edit"
          hint="3% is the default set at account setup. Leave it empty where the fee was waived."
        />
      </dl>
    ) : (
      <>
        {!buildingIsLive && (
          <p className="mb-2 text-xs text-tcs-text-secondary">
            {facility.planned_start_date
              ? `Starts ${new Date(`${facility.planned_start_date}T00:00:00`).toLocaleDateString(
                  undefined,
                  { day: 'numeric', month: 'long' },
                )} — agreed, not yet earning.`
              : 'No start date on file yet — agreed, not yet earning.'}
          </p>
        )}
        <dl className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ClientRateRow
            basis={basis}
            monthlyRate={clientRate}
            activeLineCount={activeAssignments.length}
            buildingIsLive={buildingIsLive}
            scheduledIncrease={scheduledIncrease}
            hourlyRate={hourlyRate}
            paused={isPaused}
            historyHref={
              canReadRateHistory
                ? `/money/rate-proposals?facility=${encodeURIComponent(facility.facility_id)}`
                : null
            }
          />
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
              Sub cost (monthly)
              {isPaused && <PausedFieldBadge />}
            </dt>
            <dd className="mt-1 text-sm text-tcs-text">{isPaused ? '$0.00' : subCostText}</dd>
            {isPaused ? (
              <p className="mt-0.5 text-[11px] text-tcs-text-muted">
                {resumeSubTotal > 0
                  ? resumesAtHint(resumeSubTotal)
                  : 'No sub cost recorded for when service resumes.'}
              </p>
            ) : (
              <>
                {subCostNote && <p className="mt-0.5 text-[11px] text-tcs-text-muted">{subCostNote}</p>}
                <button
                  type="button"
                  onClick={() => setShowBreakdown((s) => !s)}
                  className="mt-1 text-[11px] text-tcs-text-secondary underline-offset-2 hover:underline"
                >
                  Sum of {activeAssignments.length} {buildingIsLive ? 'active' : 'agreed'} service
                  line{activeAssignments.length === 1 ? '' : 's'} at this facility
                  {showBreakdown ? ' — hide' : ' — show'}
                </button>
                {showBreakdown && activeAssignments.length > 0 && (
                  <ul className="mt-2 space-y-1 rounded bg-tcs-bg-muted p-2 text-xs">
                    {activeAssignments.map((a) => (
                      <li key={a.assignment_id} className="flex justify-between gap-3">
                        <span className="truncate">
                          {a.worker_name ?? a.provider_name ?? a.provider_id ?? a.assignment_id}
                          {a.service_label ? ` · ${a.service_label}` : ''}
                        </span>
                        <span className="font-mono text-tcs-text">
                          {a.sub_rate_monthly != null
                            ? fmtMoney(a.sub_rate_monthly)
                            : paysSubByTheHour(a) && a.sub_hourly_rate != null
                              ? perHourShort(a.sub_hourly_rate)
                              : 'not recorded'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {flagged && (
                  <p className="mt-1 text-[11px] text-st-warn-fg">
                    ⚠ {assignments.filter((a) => a.assignment_status === 'FLAG').length} flagged
                    assignment
                    {assignments.filter((a) => a.assignment_status === 'FLAG').length === 1
                      ? ''
                      : 's'}
                    , sub cost may be incomplete.
                  </p>
                )}
              </>
            )}
          </div>
          <MarginRows
            basis={basis}
            clientRate={clientRate}
            subTotal={subTotal}
            marginMonthly={marginMonthly}
            marginPct={marginPct}
            paused={isPaused}
            resumeMarginMonthly={resumeMarginMonthly}
            resumeMarginPct={resumeMarginPct}
          />
          {basis === 'hourly' && scheduleLine && (
            <ScheduleEstimateRow
              rate={scheduleLine.billing_rate}
              hoursPerDay={scheduleLine.sub_hours_per_day}
              daysPerWeek={scheduleLine.service_days_per_week}
            />
          )}
          {basis === 'hourly' && (
            <BilledMonthsRow
              months={billed.months}
              readable={hasFinanceCapability(viewer)}
              loading={billed.loading}
              error={billed.error}
            />
          )}
          <InfoField
            label="Management fee"
            value={storedFeePct === '' ? 'None — waived' : `${storedFeePct}%`}
          />
        </dl>
        {showClusterSubsection && margin && (
          <ClusterMarginSubsection facility={facility} margin={margin} cluster={cluster} />
        )}
      </>
    );

  return (
    <div id="info-card-money" className="scroll-mt-24">
      <CardWithModeToggle
        title="Financial summary"
        icon={<FileText className="h-4 w-4 text-tcs-text-secondary" />}
        mode={mode}
        onModeChange={setMode}
        hasChanges={feeChanged}
        onSave={handleCardSave}
        onCancel={() => setFeeDraft(null)}
        description="Nothing here is typed except the management fee. Every other number comes from a process that records why it changed."
        elsewhere={elsewhere}
      >
        {moneyBody}
      </CardWithModeToggle>
    </div>
  );
}

// ============================================================================
// 2026-05-26 — Billing-cluster rendering helpers
// ============================================================================

// BundledServiceLocationCard (Branch C). Replaces the standalone Financial
// Summary card for facilities where is_billing_primary=false. The local
// service_assignments row carries sub_rate_monthly=0 by design (the real
// sub cost is pooled at the primary's assignment), so showing the legacy
// card produces misleading "100% margin" theater. This banner reframes the
// page: "billing happens elsewhere; here's the cluster's true margin; here's
// a link to the primary." Visual treatment matches ClosedFacilityBanner —
// slate/neutral, role="status", not amber/red — this is informational, not
// a warning.
function BundledServiceLocationCard({
  margin,
}: {
  margin: FacilityMarginRow;
}) {
  const primaryId = margin.cluster_primary_facility_id;
  const primaryName = margin.cluster_primary_facility_name ?? 'the primary facility';
  const pct = margin.cluster_margin_pct;
  const monthly = margin.cluster_margin_monthly;
  const count = margin.cluster_member_count;
  const formatMoney = (n: number) =>
    `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return (
    <div
      role="status"
      className="rounded-xl border border-tcs-border-strong bg-tcs-surface p-5 text-sm text-tcs-text-secondary"
    >
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-tcs-text-secondary" />
        <h3 className="text-sm font-semibold text-tcs-text">
          Billing handled at {primaryName}
        </h3>
      </div>
      <p className="mt-2 text-tcs-text-secondary">
        This facility shares a contract with{' '}
        <span className="font-medium text-tcs-text">{primaryName}</span>. The
        combined cluster margin is{' '}
        <span className="font-medium text-tcs-text">
          {pct != null ? `${Number(pct).toFixed(1)}%` : '—'}
        </span>
        {monthly != null && (
          <>
            {' '}({formatMoney(Number(monthly))}/mo
            {typeof count === 'number' ? ` across ${count} facilities` : ''})
          </>
        )}
        .
      </p>
      <p className="mt-2 text-xs text-tcs-text-secondary">
        Standalone sub cost and margin are intentionally suppressed for bundled
        service locations — the contract sub cost is recorded on the primary's
        Crew &amp; Sub card.
      </p>
      {primaryId && (
        <div className="mt-3">
          <Link
            to={`/account-management/facility/${primaryId}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border-strong bg-tcs-raised px-3 py-1.5 text-xs font-medium text-tcs-text-secondary shadow-sm hover:bg-tcs-bg-muted"
          >
            Open {primaryName}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </Link>
        </div>
      )}
    </div>
  );
}

// ClusterMarginSubsection (Branch B). Rendered below the existing standalone
// Financial Summary on cluster primaries. Visual treatment: faint border-top
// inside the same card (NOT a separate card) so a glance reads "more of the
// same financial summary." Sub-cost / margin headline numbers come from
// v_billing_cluster_margins for consistency with the cluster aggregate;
// member chip list uses member_facility_ids + member_facility_names (text[]).
// If the cluster fetch failed we degrade gracefully to v_facility_margins
// cluster_* columns + no chip list — the standalone block above still
// renders truthfully so the page is never useless.
function ClusterMarginSubsection({
  facility,
  margin,
  cluster,
}: {
  facility: Facility;
  margin: FacilityMarginRow;
  cluster: BillingClusterRow | null;
}) {
  const formatMoney = (n: number | null | undefined): string =>
    n == null
      ? '—'
      : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  // Prefer cluster row (aggregate), fall back to margin row cluster_* fields.
  const clientRate =
    cluster?.cluster_total_client_rate_monthly ?? margin.cluster_total_client_rate ?? null;
  const subCost =
    cluster?.cluster_total_sub_cost_monthly ?? margin.cluster_total_sub_cost ?? null;
  const marginMonthly =
    cluster?.cluster_margin_monthly ?? margin.cluster_margin_monthly ?? null;
  const marginPct =
    cluster?.cluster_margin_pct ?? margin.cluster_margin_pct ?? null;
  const memberCount =
    cluster?.cluster_member_count ?? margin.cluster_member_count ?? null;

  // Build chip list from the parallel ids + names arrays. Filter out this
  // facility (it's already represented by the standalone block above) so
  // the chip list reads "the other facilities in this cluster."
  const ids = cluster?.member_facility_ids ?? null;
  const names = cluster?.member_facility_names ?? null;
  const others: Array<{ id: string; name: string }> = [];
  if (Array.isArray(ids) && Array.isArray(names)) {
    for (let i = 0; i < ids.length; i += 1) {
      const id = ids[i];
      const name = names[i] ?? id;
      if (id && id !== facility.facility_id) {
        others.push({ id, name });
      }
    }
  }

  return (
    <div className="mt-4 border-t border-tcs-border pt-4">
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-tcs-text-secondary" />
        <h4 className="text-sm font-semibold text-tcs-text">Combined cluster</h4>
        {typeof memberCount === 'number' && (
          <span className="rounded-full bg-tcs-bg-muted px-2 py-0.5 text-[11px] font-medium text-tcs-text-secondary">
            {memberCount} facilities
          </span>
        )}
      </div>
      <dl className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <InfoField label="Cluster client rate (monthly)" value={formatMoney(clientRate)} />
        <InfoField label="Cluster sub cost (monthly)" value={formatMoney(subCost)} />
        <InfoField label="Cluster margin (monthly)" value={formatMoney(marginMonthly)} />
        <InfoField
          label="Cluster margin %"
          value={marginPct != null ? `${Number(marginPct).toFixed(1)}%` : null}
        />
      </dl>
      {others.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
            Other facilities in this cluster
          </div>
          <ul className="mt-2 flex flex-wrap gap-2">
            {others.map((m) => (
              <li key={m.id}>
                <Link
                  to={`/account-management/facility/${m.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-tcs-border bg-tcs-bg-muted px-2.5 py-1 text-xs text-tcs-text hover:bg-tcs-bg"
                >
                  {m.name}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-3 text-[11px] text-tcs-text-muted">
        Sub cost is pooled at the primary; non-primary facilities in this cluster
        show $0 standalone sub cost by design.
      </p>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function SectionErrorBanner({ section, message }: { section: string; message: string }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-st-warn-border-strong bg-st-warn-bg px-3 py-2 text-sm text-st-warn-fg-strong"
    >
      <span className="font-medium">{section} couldn't fully load.</span>{' '}
      <span className="text-st-warn-fg-strong">{message}</span>
    </div>
  );
}

function InfoField({
  label,
  value,
  full,
}: {
  label: string;
  value: string | null | undefined;
  full?: boolean;
}) {
  return (
    <div className={full ? 'md:col-span-2' : undefined}>
      <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
        {label}
      </dt>
      <dd className="mt-1 whitespace-pre-wrap text-sm text-tcs-text">
        {value && value.trim().length > 0 ? value : '—'}
      </dd>
    </div>
  );
}
// 2026-05-12 close-banner. Surfaces closed_date + close_reason at the top
// of Facility Detail whenever status='Closed'. We deliberately render this
// here (not in PageHeader) so it appears above the RAG dot / facility name
// and is the first thing the user sees — the page is otherwise designed
// around an Active facility and the rest of the UI could mislead an AM
// who didn't realise the account had churned. Gray slate, no destructive
// color: this is informational, not an error state.
function ClosedFacilityBanner({
  closedDate,
  closeReason,
}: {
  closedDate: string | null;
  closeReason: string | null;
}) {
  // TCSD-0070: `new Date('2026-05-01')` parses a bare DATE as UTC midnight and
  // then renders it in local time, so every date west of Greenwich comes out a
  // day early. This banner said "Closed Apr 30, 2026" directly above the new
  // planned/actual row saying "May 1, 2026" — the same fact, two dates, on one
  // screen. The local-midnight anchor now lives once, in shared/format.
  const dateLabel = closedDate ? fmtBareDate(closedDate) : 'date unknown';
  return (
    <div
      role="status"
      className="mb-3 flex flex-col gap-1 rounded-lg border border-tcs-border-strong bg-tcs-surface px-4 py-3 text-sm text-tcs-text-secondary"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-st-neutral px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-secondary">
          Closed
        </span>
        <span className="font-medium">Closed {dateLabel}</span>
      </div>
      {closeReason ? (
        <p className="text-tcs-text-secondary">{closeReason}</p>
      ) : (
        <p className="text-tcs-text-muted italic">No close reason recorded.</p>
      )}
    </div>
  );
}


// ============================================================================
// AddServiceLineModal (Task 2, 2026-07-14) — inline creation of another
// service_assignments row on a live facility: the post-build equivalent of the
// SOP intake repeater. Same field set + hourly handling (reuses the shared
// service-lines helpers). New-sub authoring stays in the /sub-replacement flow;
// here you pick an existing sub or leave it unassigned.
// ============================================================================

const ASL_INPUT =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';

function aslNum(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function AddServiceLineModal({
  open,
  facilityId,
  onClose,
  onAdded,
}: {
  open: boolean;
  facilityId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  // ⛔ No default. The old form opened on "Janitorial / Primary_Cleaner", which
  // is a form default wearing the clothes of a person's answer — 62 live records
  // carried a name the fixed drop-down of six did not even contain. The work is
  // chosen from the list or it is not chosen. Ben, 2026-09-16.
  const [serviceCode, setServiceCode] = useState('');
  const [billingMethod, setBillingMethod] = useState<NasBillingMethod>('Monthly_Flat');
  const [clientRate, setClientRate] = useState('');
  const [providerId, setProviderId] = useState<string | null>(null);
  const [workerName, setWorkerName] = useState('');
  const [subRateType, setSubRateType] = useState<NasSubRateType>('monthly');
  const [subMonthly, setSubMonthly] = useState('');
  const [hourly, setHourly] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState('');
  const [daysPerWeek, setDaysPerWeek] = useState('');
  const [startDate, setStartDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!open) return null;

  const previewMonthly =
    subRateType === 'hourly' ? computeHourlyMonthly(hourly, hoursPerDay, daysPerWeek) : aslNum(subMonthly);

  async function handleSave() {
    setSaving(true);
    setErr(null);
    try {
      // ⛔ ONE DOOR. This used to insert the row itself and mint its own id by
      // reading every ASN in the table and adding one — two account managers
      // saving in the same moment got the same number and one of them lost the
      // line. save_service_line() mints under a lock, leaves the status word to
      // the dates, and applies the same setup-versus-rate-change rule the
      // onboarding card's editor gets.
      const patch: Record<string, unknown> = {
        service_code: serviceCode,
        billing_method: billingMethod,
        billing_rate: aslNum(clientRate),
        sub_rate_type: subRateType,
        provider_id: providerId,
        worker_name: workerName.trim() || null,
        start_date: startDate || null,
        notes: notes.trim() || null,
        // ⛔ THIS ADDS A LINE (item 847). Without saying so, the door looked for
        // an existing line of the same kind and saved over it — a second Main
        // clean at Houston would have rewritten the ORs line.
        new_line: true,
      };
      if (subRateType === 'hourly') {
        patch.sub_hourly_rate = aslNum(hourly);
        patch.sub_hours_per_day = aslNum(hoursPerDay);
        patch.service_days_per_week = aslNum(daysPerWeek);
      } else {
        patch.sub_rate_monthly = aslNum(subMonthly);
      }

      const { data, error: rpcErr } = await supabase.rpc('save_service_line', {
        p_facility_id: facilityId,
        p_patch: patch,
        p_assignment_id: null,
        p_sop_id: null,
      });
      if (rpcErr) throw rpcErr;
      // ⛔ A refusal is not a save. The door answers per field with its own
      // sentence; showing it beats a green tick over a line that did not land.
      const refused = (data as { refused?: Record<string, string> } | null)?.refused ?? {};
      const sentences = Object.values(refused);
      if (sentences.length > 0) {
        setErr(sentences.join(' '));
        return;
      }
      onAdded();
      onClose();
    } catch (e) {
      console.error('[facility-detail] add service line failed', e);
      setErr(errorMessage(e, 'Could not add the service line.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 sm:items-center" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-xl bg-tcs-overlay shadow-xl sm:max-h-[88vh] sm:w-[520px] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
      >
        <header className="border-b border-tcs-border bg-tcs-bg p-4">
          <h2 className="text-base font-semibold text-tcs-text">Add service line</h2>
          <p className="mt-0.5 text-xs text-tcs-text-secondary">
            Creates another line on this facility (e.g. a day porter or monthly carpet crew).
          </p>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-tcs-text">What kind of work</span>
            {/* ⛔ The same control the project screen renders, narrowed to standing
                work. "I don't see it" is its last row and the words behind it are
                the line's own note, so nothing is stored twice. */}
            <RecurringServiceSelect
              value={serviceCode}
              onChange={setServiceCode}
              scopeText={notes}
              onScopeTextChange={setNotes}
              selectClassName={ASL_INPUT}
              inputClassName={ASL_INPUT}
              required
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-tcs-text">Billing method</span>
              <select value={billingMethod} onChange={(e) => setBillingMethod(e.target.value as NasBillingMethod)} className={ASL_INPUT}>
                {NAS_BILLING_METHOD_OPTIONS.map((b) => (<option key={b} value={b}>{b.replace(/_/g, ' ')}</option>))}
              </select>
            </label>
            <label className="block text-sm">
              {/* Per hour on a line billed by the hour (items 831/758). */}
              <span className="mb-1 block font-medium text-tcs-text">
                {billingMethod === 'Hourly' ? 'Client price ($/hr)' : 'Client price ($/mo)'}
              </span>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={clientRate} onChange={(e) => setClientRate(e.target.value)} className={ASL_INPUT} placeholder="0.00" />
            </label>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-tcs-text">Subcontractor (optional)</span>
            <SmartSelect entity="service_provider" value={providerId} onChange={(id) => setProviderId(id)} entityLabel="sub" placeholder="Search subs… (leave blank = unassigned)" isSelectable={isAssignableSub} />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-tcs-text">Worker name (optional)</span>
            <input type="text" value={workerName} onChange={(e) => setWorkerName(e.target.value)} className={ASL_INPUT} autoComplete="off" />
          </label>

          <div>
            <span className="mb-1 block text-sm font-medium text-tcs-text">Contract rate basis</span>
            <div className="flex flex-wrap gap-1.5">
              {(['monthly', 'hourly'] as NasSubRateType[]).map((t) => (
                <button key={t} type="button" onClick={() => setSubRateType(t)} className={`rounded-md border px-3 py-1.5 text-sm capitalize ${subRateType === t ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink' : 'border-tcs-border bg-tcs-raised text-tcs-text hover:bg-tcs-bg-muted'}`}>{t}</button>
              ))}
            </div>
          </div>

          {subRateType === 'monthly' ? (
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-tcs-text">Monthly sub payment ($)</span>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={subMonthly} onChange={(e) => setSubMonthly(e.target.value)} className={ASL_INPUT} placeholder="0.00" />
            </label>
          ) : (
            <div className="space-y-2">
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="block text-sm"><span className="mb-1 block font-medium text-tcs-text">Hourly ($)</span><input type="number" inputMode="decimal" step="0.01" min="0" value={hourly} onChange={(e) => setHourly(e.target.value)} className={ASL_INPUT} placeholder="0.00" /></label>
                <label className="block text-sm"><span className="mb-1 block font-medium text-tcs-text">Hrs / day</span><input type="number" inputMode="decimal" step="0.25" min="0" value={hoursPerDay} onChange={(e) => setHoursPerDay(e.target.value)} className={ASL_INPUT} placeholder="8" /></label>
                <label className="block text-sm"><span className="mb-1 block font-medium text-tcs-text">Days / wk</span><input type="number" inputMode="numeric" min="1" max="7" value={daysPerWeek} onChange={(e) => setDaysPerWeek(e.target.value)} className={ASL_INPUT} placeholder="5" /></label>
              </div>
              <p className="text-[11px] text-tcs-text-secondary">
                Est. monthly sub cost: <span className="font-mono text-tcs-text">{previewMonthly != null ? `$${previewMonthly.toLocaleString()}` : '—'}</span> — rate × hrs/day × days/wk × 52 ÷ 12.
              </p>
            </div>
          )}

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-tcs-text">Start date (optional)</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={ASL_INPUT} />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-tcs-text">Notes</span>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} className={ASL_INPUT} autoComplete="off" placeholder="Optional" />
          </label>

          {err && (
            <div className="rounded-md border border-rag-red bg-st-danger-bg px-3 py-2 text-xs text-rag-red" role="alert">{err}</div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-tcs-border bg-tcs-bg px-4 py-3">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-50">Cancel</button>
          <button type="button" onClick={() => void handleSave()} disabled={saving} className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50">
            {saving ? 'Adding…' : 'Add line'}
          </button>
        </footer>
      </div>
    </div>
  );
}
